import { gql, useLazyQuery, useMutation, useQuery } from "@apollo/client";
import {
  Button,
  Heading,
  Icon,
  Input,
  Menu,
  Select,
  Spinner,
  Switch,
  Tag,
  Text,
  Textarea,
  useToastContext,
} from "@ttc3k/trekker";
import {
  Cart,
  Check,
  EditPencil,
  FloppyDisk,
  MoreHoriz,
  OpenNewWindow,
  PlusCircle,
  Send,
  Star,
  Trash,
  WarningTriangle,
  Xmark,
} from "iconoir-react";
import { formatInTimeZone } from "date-fns-tz";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Flex, Grid } from "styled-system/jsx";
import {
  CopyToClipboard,
  DateInput,
  WhereaboutsModal,
} from "shared/components";
import { PricingSummary } from "shared/components/organisms/PricingSummary";
import { EmailActivitySlideout } from "shared/components/organisms/EmailActivitySlideout/EmailActivitySlideout";
import {
  type ActivityLogManyQuery,
  EnumDiscountUnit,
  EnumProductType,
  useOperatorByIdQuery,
  usePolicyManyQuery,
  useStorefrontByIdQuery,
} from "gql/generated";
import {
  formatDate,
  formatInvoiceMembershipPeriod,
  formatPrice,
  getMultilingualString,
  getPerTicketDiscountCount,
  getResolvedProductSellingConfig,
} from "shared/utils";
import {
  useBookerContext,
  useCartContext,
  useInvoiceAdminBilling,
} from "widgets/booker/contexts";
import { useCartPersistence } from "widgets/booker/contexts/CartContextProvider";
import { addressSearchValueToAddressInput } from "shared/components/organisms/SurveyQuestionInput/addressSearchResponse";
import { resolveLegacyReceiptPdfUrl } from "shared/utils/legacySilkstartPdf";
import type { InvoiceAdminAppliedDiscount } from "widgets/booker/contexts/InvoiceAdminBillingContext";
import { buildRenewalSettingsChangelogLines } from "./renewalInvoiceChangelog";
import { InvoiceRecordPaymentModal } from "apps/storefront/OrderDetails/InvoiceRecordPaymentModal";
import { InvoiceJournalModal } from "apps/storefront/OrderDetails/InvoiceJournalModal";
import {
  buildSyntheticPaymentHistoryEntries,
  consolidateRecordPaymentHistory,
  describeInvoiceJournalPayment,
  sortInvoiceJournalEntries,
} from "apps/storefront/OrderDetails/invoiceJournalTimeline";
import { useFeatureFlag } from "shared/contextProviders/featureFlags";
import { FeatureFlags } from "shared/featureFlags";

const PAYMENT_REQUEST_BY_ID = gql`
  query AdminPaymentRequestById($paymentRequestId: MongoID) {
    paymentRequestById(paymentRequestId: $paymentRequestId) {
      _id
      invoiceNumber
      booker
      order
      status
      version
      needsAttention
      dueDate
      dueTermDays
      scheduledSendDate
      sentAt
      grandTotal
      productSubtotal
      lineItemSubtotal
      taxAmount
      taxLines {
        name
        ratePercent
        amount
      }
      customerFee
      currency
      amountPaid
      balanceDue
      creditBalance
      balanceStatus
      internalAccountingNotes
      externalReference
      accountingDate
      notes
      invoicePolicy
      accountingCode
      productNameOverride
      taxExempt
      cancellationNote
      renewablePurchaseId
      nextRenewalDate
      recipient
      recipientContact {
        firstName
        lastName
        email
        phone
        address {
          line1
          line2
          city
          subdivision
          postalCode
          country
          location {
            type
            coordinates
          }
        }
      }
      additionalLineItems {
        description
        amount
        quantity
      }
      appliedDiscount {
        discountId
        name
        promoCode
        unit
        amount
        discountAmount
        isOngoing
      }
      changeHistory {
        eventId
        action
        timestamp
        recordedAt
        effectiveAt
        performedByName
        note
        activityLogId
        references {
          paymentEntryId
        }
      }
      paymentEntries {
        _id
        type
        amount
        currency
        method
        receivedDate
        effectiveAt
        recordedAt
        reference
        chequeNumber
        cardBrand
        cardLast4
      }
      cartSnapshot {
        cart {
          productName
          productId
          productType
          startDateISO
          endDateISO
          total
          guestsPerTier {
            count
            tierId
            sessionId
          }
          quantity
          sessionId
          sessionIds
          merchVariantId
          merchVariantLabel
        }
        selectedBillingInterval
        selectedTierId
        selectedOperatorId
        businessField
        guestNames
        guests {
          name
          email
        }
        promoCode
        customer {
          firstName
          lastName
          email
          phone
          complete
        }
        customerComplete
        questionnaireResponses {
          response
          questionData
        }
        questionnaireComplete
        acceptedPolicies
        customerAddress
      }
      kind
      statementPeriod
      statementLines {
        orderId
        serviceDate
        subTotal
        grandTotal
      }
    }
  }
`;

const PAYMENT_REQUEST_STATEMENT_REMOVE_ORDER = gql`
  mutation AdminPaymentRequestStatementRemoveOrder(
    $paymentRequestId: MongoID!
    $orderId: String!
  ) {
    paymentRequestStatementRemoveOrder(
      paymentRequestId: $paymentRequestId
      orderId: $orderId
    ) {
      _id
      status
    }
  }
`;

const RENEWAL_RECORD_BY_PAYMENT_REQUEST = gql`
  query AdminRenewalRecordByPaymentRequest($paymentRequestId: MongoID!) {
    renewalRecordByPaymentRequest(paymentRequestId: $paymentRequestId) {
      _id
      periodStart
      periodEnd
    }
  }
`;

const PAYMENT_REQUEST_PRICE_COMPUTE = gql`
  query AdminPaymentRequestPriceCompute(
    $bookerId: MongoID!
    $productSubtotal: Int!
    $additionalLineItems: JSON
    $appliedDiscount: JSON
    $taxExempt: Boolean
    $paymentRequestId: MongoID
  ) {
    paymentRequestPriceCompute(
      bookerId: $bookerId
      productSubtotal: $productSubtotal
      additionalLineItems: $additionalLineItems
      appliedDiscount: $appliedDiscount
      taxExempt: $taxExempt
      paymentRequestId: $paymentRequestId
    ) {
      productSubtotal
      lineItemSubtotal
      discountAmount
      subTotal
      taxLines {
        name
        ratePercent
        amount
      }
      taxAmount
      customerFee
      grandTotal
    }
  }
`;

const PAYMENT_REQUEST_UPDATE = gql`
  mutation AdminPaymentRequestUpdate(
    $paymentRequestId: MongoID!
    $version: Int!
    $record: JSON!
  ) {
    paymentRequestUpdate(
      paymentRequestId: $paymentRequestId
      version: $version
      record: $record
    ) {
      _id
      status
      version
    }
  }
`;

const PAYMENT_REQUEST_SEND = gql`
  mutation AdminPaymentRequestSend(
    $paymentRequestId: MongoID!
    $version: Int!
  ) {
    paymentRequestSend(paymentRequestId: $paymentRequestId, version: $version) {
      _id
      status
      version
    }
  }
`;

const PAYMENT_REQUEST_RESEND = gql`
  mutation AdminPaymentRequestResend(
    $paymentRequestId: MongoID!
    $version: Int!
    $note: String
    $idempotencyKey: String
  ) {
    paymentRequestResend(
      paymentRequestId: $paymentRequestId
      version: $version
      note: $note
      idempotencyKey: $idempotencyKey
    ) {
      _id
      status
      version
    }
  }
`;

const PAYMENT_REQUEST_DELETE = gql`
  mutation AdminPaymentRequestDelete($paymentRequestId: MongoID!) {
    paymentRequestDelete(paymentRequestId: $paymentRequestId) {
      _id
    }
  }
`;

const PAYMENT_REQUEST_SET_NEEDS_ATTENTION = gql`
  mutation AdminPaymentRequestSetNeedsAttention(
    $paymentRequestId: MongoID!
    $needsAttention: Boolean!
  ) {
    paymentRequestSetNeedsAttention(
      paymentRequestId: $paymentRequestId
      needsAttention: $needsAttention
    ) {
      _id
      needsAttention
    }
  }
`;

const PAYMENT_REQUEST_ADD_INTERNAL_NOTE = gql`
  mutation AdminPaymentRequestAddInternalNote(
    $paymentRequestId: MongoID!
    $note: String!
  ) {
    paymentRequestAddInternalNote(
      paymentRequestId: $paymentRequestId
      note: $note
    ) {
      _id
      changeHistory {
        eventId
        action
        timestamp
        recordedAt
        effectiveAt
        performedByName
        note
        activityLogId
        references {
          paymentEntryId
        }
      }
    }
  }
`;

const PAYMENT_REQUEST_CANCEL = gql`
  mutation AdminPaymentRequestCancel(
    $paymentRequestId: MongoID!
    $version: Int!
    $cancellationNote: String!
  ) {
    paymentRequestCancel(
      paymentRequestId: $paymentRequestId
      version: $version
      cancellationNote: $cancellationNote
    ) {
      _id
      status
      version
      cancellationNote
    }
  }
`;

const PAYMENT_REQUEST_MARK_PAID = gql`
  mutation AdminPaymentRequestMarkPaid(
    $paymentRequestId: MongoID!
    $version: Int!
    $note: String
  ) {
    paymentRequestMarkPaid(
      paymentRequestId: $paymentRequestId
      version: $version
      note: $note
    ) {
      _id
      status
      version
      order
    }
  }
`;

const PAYMENT_REQUEST_SUBMIT_FOR_REVIEW = gql`
  mutation AdminPaymentRequestSubmitForReview(
    $paymentRequestId: MongoID!
    $version: Int!
  ) {
    paymentRequestSubmitForReview(
      paymentRequestId: $paymentRequestId
      version: $version
    ) {
      _id
      status
      version
    }
  }
`;

const PAYMENT_REQUEST_MARK_PENDING = gql`
  mutation AdminPaymentRequestMarkPending(
    $paymentRequestId: MongoID!
    $version: Int!
    $note: String
  ) {
    paymentRequestMarkPending(
      paymentRequestId: $paymentRequestId
      version: $version
      note: $note
    ) {
      _id
      status
      version
    }
  }
`;

const PAYMENT_REQUEST_UNDO_PENDING = gql`
  mutation AdminPaymentRequestUndoPending(
    $paymentRequestId: MongoID!
    $version: Int!
    $note: String
  ) {
    paymentRequestUndoPending(
      paymentRequestId: $paymentRequestId
      version: $version
      note: $note
    ) {
      _id
      status
      version
      order
    }
  }
`;

const ACTIVITY_LOG_BY_ID = gql`
  query AdminInvoiceActivityLogById($id: MongoID!) {
    activityLogById(_id: $id) {
      _id
      entityName {
        en
        fr
        es
      }
      message {
        en
        fr
        es
      }
      initiatedBy
      type
      createdDateISO
      entity {
        _entityId
        type
      }
      owner {
        entityId
        type
      }
      emailActivity {
        sentHtml
        templatePreviewHtml
        fromAddress {
          name
          email
        }
        subject
        recipients {
          userId
          personId
          email
          displayName
          deliveredAt
          openedAt
          clickedAt
          bouncedAt
          droppedAt
          deferredAt
          spamReportedAt
        }
      }
    }
  }
`;

type EditableLineItem = {
  description: string;
  /** Dollars as typed by the user (e.g. "10.17", "-1.61"). Parsed to cents for API. */
  amountDollarsStr: string;
  quantity: number;
};

/** Cart and payment-request amounts are stored in cents. */
const parseDollarsStrToCents = (raw: string): number => {
  const t = raw.trim();
  if (t === "" || t === "-" || t === "." || t === "-.") return 0;
  const n = Number.parseFloat(t);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100);
};

const centsToDollarsInput = (cents: number): string => {
  if (!Number.isFinite(cents) || cents === 0) return "";
  return (cents / 100).toFixed(2);
};

const sanitizeAmountDollarsInput = (raw: string): string => {
  let s = "";
  let seenDot = false;
  for (let i = 0; i < raw.length; i++) {
    const c = raw[i];
    if (c === "-" && i === 0) {
      s += c;
      continue;
    }
    if (c === "." && !seenDot) {
      seenDot = true;
      s += c;
      continue;
    }
    if (/\d/.test(c)) {
      s += c;
    }
  }
  const dotI = s.indexOf(".");
  if (dotI >= 0) {
    const head = s.slice(0, dotI + 1);
    const tail = s
      .slice(dotI + 1)
      .replace(/\./g, "")
      .slice(0, 2);
    s = head + tail;
  }
  return s;
};

/** Sentinel select value representing "no accounting code" (empty string can't be a Select item value). */
const ACCOUNTING_CODE_NONE_VALUE = "__none__";

/** Due-date dropdown values: "none", a number of days as a string, or "custom". */
const DUE_DATE_NONE_VALUE = "none";
const DUE_DATE_CUSTOM_VALUE = "custom";
const DUE_DATE_RENEWAL_VALUE = "renewal";
const DUE_DATE_TERM_DAYS = [15, 30, 60, 90];

const isoDateInDays = (days: number): string => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const resolveDueDateModeFromPaymentRequest = (args: {
  dueTermDays?: number | null;
  dueDate?: string | null;
  isRenewalInvoice: boolean;
  renewalDate: string;
}): string => {
  const { dueTermDays, dueDate, isRenewalInvoice, renewalDate } = args;
  if (dueTermDays && dueTermDays > 0) {
    return String(dueTermDays);
  }
  const dueDateStr = dueDate?.slice(0, 10) ?? "";
  if (isRenewalInvoice && renewalDate) {
    if (!dueDateStr || dueDateStr === renewalDate) {
      return DUE_DATE_RENEWAL_VALUE;
    }
  }
  if (dueDateStr) {
    return DUE_DATE_CUSTOM_VALUE;
  }
  if (isRenewalInvoice && renewalDate) {
    return DUE_DATE_RENEWAL_VALUE;
  }
  return DUE_DATE_NONE_VALUE;
};

/** Policies longer than this read poorly on a single-page PDF invoice. */
const POLICY_WORD_COUNT_WARNING_THRESHOLD = 100;

const countWords = (text: string): number => {
  const trimmed = text.trim();
  return trimmed.length === 0 ? 0 : trimmed.split(/\s+/).length;
};

const EMPTY_LINE_ITEM: EditableLineItem = {
  description: "",
  amountDollarsStr: "",
  quantity: 1,
};

const getStatusTag = (status?: string) => {
  switch (status) {
    case "DRAFT":
      return {
        label: "Draft",
        visual: "highlight" as const,
        mode: "light" as const,
      };
    case "FOR_REVIEW":
      return {
        label: "For Review",
        visual: "accent.blue" as const,
        mode: "light" as const,
      };
    case "SENT":
      return {
        label: "Sent",
        visual: "warning" as const,
        mode: "light" as const,
      };
    case "OVERDUE":
      return {
        label: "Overdue",
        visual: "warning" as const,
        mode: "solid" as const,
      };
    case "PENDING":
      return {
        label: "Pending",
        visual: "warning" as const,
        mode: "light" as const,
      };
    case "PAID":
      return {
        label: "Paid",
        visual: "success" as const,
        mode: "solid" as const,
      };
    case "CANCELLED":
      return {
        label: "Cancelled",
        visual: "highlight" as const,
        mode: "light" as const,
      };
    default:
      return {
        label: status ?? "Unknown",
        visual: "highlight" as const,
        mode: "light" as const,
      };
  }
};

const formatChangeHistoryAction = (action?: string) => {
  if (action === "internal_note") return "Note";
  if (action === "PAYMENT_REVERSED" || action === "payment_reversed") {
    return "Payment voided";
  }
  if (action === "paid") return "Marked as paid";
  if (action === "payment_recorded") return "Payment recorded";
  return (action ?? "")
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map((word, index) =>
      index === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word,
    )
    .join(" ");
};

const isRedundantPaymentRecordedNote = (
  note?: string | null,
  paymentDetail?: string,
) => {
  if (!note?.trim()) return false;
  if (/^payment recorded by\b/i.test(note.trim())) return true;
  if (paymentDetail && note.trim() === paymentDetail) return true;
  return false;
};

export const AdminInvoicePanel = () => {
  const accountingControlsEnabled = useFeatureFlag(
    FeatureFlags.StorefrontAccountingControls,
  );
  const { toastFactory } = useToastContext();
  const { t } = useTranslation("booker", { keyPrefix: "INVOICE_ADMIN" });
  const {
    paymentRequestId,
    onCloseBookerModal,
    booker,
    isStorefrontOwner,
    storefrontId,
  } = useBookerContext();
  const {
    recipientContact,
    recipientPersonId,
    dueDate,
    setDueDate,
    scheduledSendDate,
    hydrateFromPaymentRequest,
    setBillingReadOnly,
    appliedDiscount,
    setAppliedDiscount,
  } = useInvoiceAdminBilling();
  const { serializeCart, restoreCart } = useCartPersistence();
  const {
    selectedOperatorId,
    subTotal,
    discounts: cartDiscounts,
    businessField,
    customerAddress,
    selectedTierId,
  } = useCartContext();
  const [activePaymentRequestId, setActivePaymentRequestId] = useState(
    paymentRequestId ?? "",
  );
  const [additionalLineItems, setAdditionalLineItems] = useState<
    EditableLineItem[]
  >([]);
  const [notes, setNotes] = useState("");
  const [notesHighlighted, setNotesHighlighted] = useState(false);
  /** "none" | "15" | "30" | "60" | "90" | "custom" — how the due date is chosen. */
  const [dueDateMode, setDueDateMode] = useState<string>(DUE_DATE_CUSTOM_VALUE);
  const [autoOpenCustomDueDate, setAutoOpenCustomDueDate] = useState(false);
  const [renewalDate, setRenewalDate] = useState("");
  const [savedRenewalDate, setSavedRenewalDate] = useState("");
  const [savedTierId, setSavedTierId] = useState("");
  const [invoicePolicyText, setInvoicePolicyText] = useState("");
  const [accountingCode, setAccountingCode] = useState("");
  const [productNameOverride, setProductNameOverride] = useState("");
  const [isEditingProductName, setIsEditingProductName] = useState(false);
  const [productNameDraft, setProductNameDraft] = useState("");
  const [taxExempt, setTaxExempt] = useState(false);
  const [cancellationNote, setCancellationNote] = useState("");
  /** Admin explicitly removed the discount — suppresses the cart-derived
   * fallback so the invoice bills full price until another is picked. */
  const [discountRemoved, setDiscountRemoved] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [recordPaymentModalOpen, setRecordPaymentModalOpen] = useState(false);
  const [settleModalOpen, setSettleModalOpen] = useState(false);
  const [isPreparingSettle, setIsPreparingSettle] = useState(false);
  const [journalModalOpen, setJournalModalOpen] = useState(false);
  const [resendModalOpen, setResendModalOpen] = useState(false);
  const [resendNote, setResendNote] = useState("");
  const resendIdempotencyKeyRef = useRef(crypto.randomUUID());
  const [legacyMarkPaidModalOpen, setLegacyMarkPaidModalOpen] = useState(false);
  const [legacyMarkPaidNote, setLegacyMarkPaidNote] = useState("");
  const [undoPendingModalOpen, setUndoPendingModalOpen] = useState(false);
  const [revisionModalOpen, setRevisionModalOpen] = useState(false);
  const [revisionNote, setRevisionNote] = useState("");
  const [notifyRecipientOnRevision, setNotifyRecipientOnRevision] =
    useState(true);
  const [editInvoiceNumberModalOpen, setEditInvoiceNumberModalOpen] =
    useState(false);
  const [invoiceNumberLockedModalOpen, setInvoiceNumberLockedModalOpen] =
    useState(false);
  const [invoiceNumberInput, setInvoiceNumberInput] = useState("");
  const [invoiceNumberError, setInvoiceNumberError] = useState<string | null>(
    null,
  );
  const [sendError, setSendError] = useState<string | null>(null);
  const [isInternalNoteEditorOpen, setIsInternalNoteEditorOpen] = useState(false);
  const [internalNoteDraft, setInternalNoteDraft] = useState("");
  const [emailSlideoutOpen, setEmailSlideoutOpen] = useState(false);
  const [emailActivityLog, setEmailActivityLog] = useState<
    ActivityLogManyQuery["activityLogMany"][0] | null
  >(null);
  const hydratedPaymentRequestIdRef = useRef<string | null>(null);
  const hydratedBillingPaymentRequestIdRef = useRef<string | null>(null);
  const hydratedPolicyPaymentRequestIdRef = useRef<string | null>(null);
  const hydratedDiscountPaymentRequestIdRef = useRef<string | null>(null);
  /** The changelog block currently auto-managed inside `notes` (unsaved). */
  const autoChangelogRef = useRef("");

  const [fetchActivityLog] = useLazyQuery(ACTIVITY_LOG_BY_ID);
  const { data: selectedOperatorData } = useOperatorByIdQuery({
    variables: { operatorId: selectedOperatorId ?? "" },
    skip: !selectedOperatorId,
  });
  const { data: storefrontData } = useStorefrontByIdQuery({
    variables: { id: storefrontId ?? "" },
    skip: !storefrontId,
  });
  const accountingCodeOptions = useMemo(
    () =>
      (storefrontData?.storefrontById?.accountingCodes ?? []).filter(
        (code): code is string => !!code,
      ),
    [storefrontData?.storefrontById?.accountingCodes],
  );
  const accountingCodeCollection = useMemo(() => {
    const knownCodes =
      accountingCode && !accountingCodeOptions.includes(accountingCode)
        ? [accountingCode, ...accountingCodeOptions]
        : accountingCodeOptions;
    return Select.createCollection({
      items: [
        { label: t("ACCOUNTING_CODE_NONE"), value: ACCOUNTING_CODE_NONE_VALUE },
        ...knownCodes.map(code => ({ label: code, value: code })),
      ],
    });
  }, [accountingCodeOptions, accountingCode, t]);
  const customProductNameOptions = useMemo(
    () =>
      (storefrontData?.storefrontById?.customProductNames ?? []).filter(
        (name): name is string => !!name,
      ),
    [storefrontData?.storefrontById?.customProductNames],
  );
  const customProductNameCollection = useMemo(
    () =>
      Select.createCollection({
        items: customProductNameOptions.map(name => ({
          label: name,
          value: name,
        })),
      }),
    [customProductNameOptions],
  );


  const { data: policyData, loading: policiesLoading } = usePolicyManyQuery({
    variables: { filter: { storefront: storefrontId, isActive: true } },
    skip: !storefrontId,
  });
  const policyOptions = useMemo(
    () => policyData?.policyMany ?? [],
    [policyData?.policyMany],
  );
  const policyTemplateCollection = useMemo(
    () =>
      Select.createCollection({
        items: policyOptions.map(policy => ({
          label:
            getMultilingualString(policy?.name ?? {}) || t("POLICY_UNTITLED"),
          value: policy?._id ?? "",
        })),
      }),
    [policyOptions, t],
  );
  const invoicePolicyWordCount = useMemo(
    () => countWords(invoicePolicyText),
    [invoicePolicyText],
  );
  const isInvoicePolicyLengthy =
    invoicePolicyWordCount > POLICY_WORD_COUNT_WARNING_THRESHOLD;

  const mainProductType = booker?.bundles?.[0]?.product?.type;
  const isRenewableFee = mainProductType === EnumProductType.RenewableFee;
  const renewalProduct = booker?.bundles?.[0]?.product;
  const renewalSellingConfig = renewalProduct
    ? getResolvedProductSellingConfig(
        renewalProduct as Parameters<typeof getResolvedProductSellingConfig>[0],
      )
    : null;
  const renewalTierOptions = useMemo(() => {
    if (!renewalSellingConfig?.options?.length) return [];
    return renewalSellingConfig.options.filter(
      tier => tier.isActive !== false || tier._id === selectedTierId,
    );
  }, [renewalSellingConfig?.options, selectedTierId]);
  const selectedRenewalTierName = useMemo(() => {
    const tier = renewalTierOptions.find(
      option => option._id === selectedTierId,
    );
    return tier ? getMultilingualString(tier.name ?? {}) : "";
  }, [renewalTierOptions, selectedTierId]);
  const savedRenewalTierName = useMemo(() => {
    const tier = renewalTierOptions.find(option => option._id === savedTierId);
    return tier ? getMultilingualString(tier.name ?? {}) : "";
  }, [renewalTierOptions, savedTierId]);
  const isFixedDateRenewal =
    renewalSellingConfig?.recurring?.renewalType === "FIXED_DATE";

  const openEmailActivity = useCallback(
    async (activityLogId: string) => {
      const { data } = await fetchActivityLog({
        variables: { id: activityLogId },
      });
      const log = data?.activityLogById;
      if (log) {
        setEmailActivityLog(log as ActivityLogManyQuery["activityLogMany"][0]);
        setEmailSlideoutOpen(true);
      }
    },
    [fetchActivityLog],
  );

  const { data, loading, error, refetch } = useQuery(PAYMENT_REQUEST_BY_ID, {
    variables: { paymentRequestId: activePaymentRequestId },
    skip: !activePaymentRequestId,
    fetchPolicy: "network-only",
  });
  const isRenewalInvoice = !!data?.paymentRequestById?.renewablePurchaseId;

  const dueDateModeCollection = useMemo(() => {
    // Renewal-generated invoices can carry a non-standard inherited term
    // (e.g. 45 days); surface it as an extra option so it round-trips.
    const termDays = [...DUE_DATE_TERM_DAYS];
    const currentTerm = Number(dueDateMode);
    if (
      Number.isFinite(currentTerm) &&
      currentTerm > 0 &&
      !termDays.includes(currentTerm)
    ) {
      termDays.push(currentTerm);
      termDays.sort((a, b) => a - b);
    }
    return Select.createCollection({
      items: [
        ...(isRenewalInvoice
          ? [
              {
                label: t("DUE_DATE_RENEWAL"),
                value: DUE_DATE_RENEWAL_VALUE,
              },
            ]
          : []),
        { label: t("DUE_DATE_NONE"), value: DUE_DATE_NONE_VALUE },
        ...termDays.map(days => ({
          label: t("DUE_DATE_DAYS", { count: days }),
          value: String(days),
        })),
        { label: t("DUE_DATE_CUSTOM"), value: DUE_DATE_CUSTOM_VALUE },
      ],
    });
  }, [t, dueDateMode, isRenewalInvoice]);
  const dueDateTermDays = Number(dueDateMode);
  const isDueDateTermMode =
    Number.isFinite(dueDateTermDays) && dueDateTermDays > 0;
  const isDueDateRenewalMode = dueDateMode === DUE_DATE_RENEWAL_VALUE;

  const { data: renewalRecordData } = useQuery(
    RENEWAL_RECORD_BY_PAYMENT_REQUEST,
    {
      variables: { paymentRequestId: activePaymentRequestId },
      skip: !activePaymentRequestId || !isRenewalInvoice,
    },
  );

  const renewalPeriodLabel = useMemo(() => {
    const record = renewalRecordData?.renewalRecordByPaymentRequest;
    return (
      formatInvoiceMembershipPeriod({
        periodStart: record?.periodStart,
        periodEnd: record?.periodEnd,
        fallbackEndDate: savedRenewalDate,
      }) ?? "—"
    );
  }, [renewalRecordData?.renewalRecordByPaymentRequest, savedRenewalDate]);

  // When the admin picks a new (unsaved) renewal date, preview the anticipated
  // period: the period start is unchanged; the chosen date becomes its end.
  const previewRenewalPeriodLabel = useMemo(() => {
    if (!renewalDate || !savedRenewalDate || renewalDate === savedRenewalDate) {
      return null;
    }
    const record = renewalRecordData?.renewalRecordByPaymentRequest;
    return formatInvoiceMembershipPeriod({
      periodStart: record?.periodStart,
      periodEnd: renewalDate,
    });
  }, [
    renewalRecordData?.renewalRecordByPaymentRequest,
    renewalDate,
    savedRenewalDate,
  ]);

  const summaryMembershipPeriod = useMemo(() => {
    if (!isRenewableFee) return null;
    return (
      previewRenewalPeriodLabel ??
      (renewalPeriodLabel !== "—" ? renewalPeriodLabel : null)
    );
  }, [isRenewableFee, previewRenewalPeriodLabel, renewalPeriodLabel]);

  const [updatePaymentRequest, { loading: isSaving }] = useMutation(
    PAYMENT_REQUEST_UPDATE,
  );
  const [sendPaymentRequest, { loading: isSending }] =
    useMutation(PAYMENT_REQUEST_SEND);
  const [resendPaymentRequest, { loading: isResending }] = useMutation(
    PAYMENT_REQUEST_RESEND,
  );
  const [markPaymentRequestPaid, { loading: isMarkingPaid }] = useMutation(
    PAYMENT_REQUEST_MARK_PAID,
  );
  const [deletePaymentRequest] = useMutation(PAYMENT_REQUEST_DELETE);
  const [setNeedsAttention, { loading: isSettingNeedsAttention }] =
    useMutation(PAYMENT_REQUEST_SET_NEEDS_ATTENTION, {
      refetchQueries: ["StorefrontOrderInvoiceFeed"],
    });
  const [addInternalNote, { loading: isAddingInternalNote }] = useMutation(
    PAYMENT_REQUEST_ADD_INTERNAL_NOTE,
  );
  const [cancelPaymentRequest, { loading: isCancelling }] = useMutation(
    PAYMENT_REQUEST_CANCEL,
  );
  const [submitForReview, { loading: isSubmitting }] = useMutation(
    PAYMENT_REQUEST_SUBMIT_FOR_REVIEW,
  );
  const [markPending, { loading: isMarkingPending }] = useMutation(
    PAYMENT_REQUEST_MARK_PENDING,
  );
  const [undoPending, { loading: isUndoingPending }] = useMutation(
    PAYMENT_REQUEST_UNDO_PENDING,
  );
  const [updateInvoiceNumber, { loading: isSavingInvoiceNumber }] = useMutation(
    PAYMENT_REQUEST_UPDATE,
  );
  const [removeStatementOrder, { loading: isRemovingStatementOrder }] =
    useMutation(PAYMENT_REQUEST_STATEMENT_REMOVE_ORDER, {
      refetchQueries: ["StorefrontOrderInvoiceFeed"],
    });
  const [removingStatementOrderId, setRemovingStatementOrderId] = useState<
    string | null
  >(null);

  const paymentRequest = data?.paymentRequestById;
  const isStatement = paymentRequest?.kind === "STATEMENT";
  const statementLines: {
    orderId: string;
    serviceDate?: string | null;
    subTotal: number;
    grandTotal: number;
  }[] = (paymentRequest?.statementLines ?? []).filter(Boolean);
  const statementSubtotal = statementLines.reduce(
    (sum, line) => sum + line.subTotal,
    0,
  );
  const status = paymentRequest?.status;
  const version = paymentRequest?.version ?? 0;
  const tag = getStatusTag(status);

  useEffect(() => {
    if (!paymentRequest) return;
    if (hydratedBillingPaymentRequestIdRef.current === paymentRequest._id) {
      return;
    }

    hydrateFromPaymentRequest(paymentRequest);

    setAdditionalLineItems(
      paymentRequest.additionalLineItems?.length
        ? paymentRequest.additionalLineItems.map(
            (item: {
              description?: string | null;
              amount?: number | null;
              quantity?: number | null;
            }) => ({
              description: item.description ?? "",
              amountDollarsStr: centsToDollarsInput(Number(item.amount ?? 0)),
              quantity: Number(item.quantity ?? 1),
            }),
          )
        : [],
    );
    setNotes(paymentRequest.notes ?? "");
    setAccountingCode(paymentRequest.accountingCode ?? "");
    setProductNameOverride(paymentRequest.productNameOverride ?? "");
    setIsEditingProductName(false);
    setTaxExempt(!!paymentRequest.taxExempt);
    setCancellationNote(paymentRequest.cancellationNote ?? "");
    const nextRenewal = paymentRequest.nextRenewalDate?.slice(0, 10) ?? "";
    setRenewalDate(nextRenewal);
    setSavedRenewalDate(nextRenewal);
    setSavedTierId(paymentRequest.cartSnapshot?.selectedTierId ?? "");
    setNotesHighlighted(false);
    autoChangelogRef.current = "";
    // A saved "Net N days" term round-trips into the dropdown; renewal invoices
    // default to a fixed due date on the renewal date unless a term was saved.
    setDueDateMode(
      resolveDueDateModeFromPaymentRequest({
        dueTermDays: paymentRequest.dueTermDays,
        dueDate: paymentRequest.dueDate,
        isRenewalInvoice: !!paymentRequest.renewablePurchaseId,
        renewalDate: nextRenewal,
      }),
    );
    setAutoOpenCustomDueDate(false);
    hydratedBillingPaymentRequestIdRef.current = paymentRequest._id;
  }, [paymentRequest, hydrateFromPaymentRequest]);

  useEffect(() => {
    if (dueDateMode !== DUE_DATE_RENEWAL_VALUE || !renewalDate) return;
    setDueDate(renewalDate);
  }, [dueDateMode, renewalDate, setDueDate]);

  // Discount hydration: restore whatever is already saved on the invoice so
  // PricingSummary / save payload stay in sync. Widget cart discounts flow in
  // automatically; on renewable-fee widgets the admin can also pick one of the
  // widget's discounts manually (see the Discount section below).
  useEffect(() => {
    if (!paymentRequest) return;
    if (hydratedDiscountPaymentRequestIdRef.current === paymentRequest._id) {
      return;
    }
    setDiscountRemoved(false);
    if (paymentRequest.appliedDiscount) {
      setAppliedDiscount({
        discountId: paymentRequest.appliedDiscount.discountId,
        name: paymentRequest.appliedDiscount.name || "Discount",
        promoCode: paymentRequest.appliedDiscount.promoCode ?? undefined,
        unit: paymentRequest.appliedDiscount.unit as EnumDiscountUnit,
        amount: paymentRequest.appliedDiscount.amount,
        computedAmount:
          typeof paymentRequest.appliedDiscount.discountAmount === "number"
            ? paymentRequest.appliedDiscount.discountAmount
            : undefined,
        isOngoing: !!paymentRequest.appliedDiscount.isOngoing,
      });
    } else {
      setAppliedDiscount(null);
    }
    hydratedDiscountPaymentRequestIdRef.current = paymentRequest._id;
  }, [paymentRequest]);

  useEffect(() => {
    if (!paymentRequest) return;
    if (hydratedPolicyPaymentRequestIdRef.current === paymentRequest._id) {
      return;
    }
    // Wait for policy data to resolve so a default policy doesn't flash in empty first.
    if (policiesLoading) return;

    const existingPolicyText = paymentRequest.invoicePolicy?.trim();
    if (existingPolicyText) {
      setInvoicePolicyText(existingPolicyText);
    } else if (["DRAFT", "FOR_REVIEW"].includes(paymentRequest.status)) {
      // Unsent invoices (drafts and generated renewal invoices awaiting
      // review) pre-fill with the storefront's default policy so it is
      // snapshotted onto the invoice when saved/sent.
      const defaultPolicyId =
        storefrontData?.storefrontById?.defaultInvoicePolicy;
      const defaultPolicy = defaultPolicyId
        ? policyOptions.find(policy => policy?._id === defaultPolicyId)
        : undefined;
      setInvoicePolicyText(
        defaultPolicy ? getMultilingualString(defaultPolicy.content ?? {}) : "",
      );
    } else {
      setInvoicePolicyText("");
    }
    hydratedPolicyPaymentRequestIdRef.current = paymentRequest._id;
  }, [
    paymentRequest,
    policiesLoading,
    policyOptions,
    storefrontData?.storefrontById?.defaultInvoicePolicy,
  ]);

  useEffect(() => {
    setBillingReadOnly(
      paymentRequest?.status === "PAID" ||
        paymentRequest?.status === "CANCELLED",
    );
  }, [paymentRequest?.status, setBillingReadOnly]);

  // Clear the inline send-validation message once the missing fields are filled.
  useEffect(() => {
    if (sendError && recipientContact.email?.trim()) {
      setSendError(null);
    }
  }, [sendError, recipientContact.email]);

  useEffect(() => {
    if (!paymentRequest?.cartSnapshot) return;
    if (hydratedPaymentRequestIdRef.current === paymentRequest._id) return;

    restoreCart(paymentRequest.cartSnapshot);
    hydratedPaymentRequestIdRef.current = paymentRequest._id;
  }, [paymentRequest, restoreCart]);

  const cartDiscountTotal = useMemo(
    () => cartDiscounts.reduce((sum, d) => sum + (d.total ?? 0), 0),
    [cartDiscounts],
  );

  // Cart `subTotal` from bookerCartPrice is already post-discount. Invoice
  // pricing expects a pre-discount product subtotal + an appliedDiscount line,
  // so restore the pre-discount amount when cart discounts are present.
  const productSubtotalPreDiscount = subTotal + cartDiscountTotal;

  const cartDerivedDiscount =
    useMemo((): InvoiceAdminAppliedDiscount | null => {
      if (cartDiscounts.length === 0 || cartDiscountTotal <= 0) return null;
      const primary = cartDiscounts.reduce((best, d) =>
        (d.total ?? 0) > (best.total ?? 0) ? d : best,
      );
      const discountDoc = booker?.discounts?.find(
        d => d._id === primary.discountId,
      );
      if (!discountDoc) return null;
      return {
        discountId: discountDoc._id,
        name: getMultilingualString(discountDoc.name),
        promoCode: discountDoc.promoCode ?? undefined,
        unit: discountDoc.unit,
        amount: discountDoc.amount,
        computedAmount: cartDiscountTotal,
        isOngoing: !!discountDoc.isOngoing,
        // Stash perTicket via computedAmount > amount heuristic in the label
        // helper; keep amount as the configured rate for × N display.
      };
    }, [cartDiscounts, cartDiscountTotal, booker?.discounts]);

  const effectiveAppliedDiscount =
    appliedDiscount ?? (discountRemoved ? null : cartDerivedDiscount);

  const additionalLineItemSubtotal = useMemo(
    () =>
      additionalLineItems.reduce(
        (sum, item) =>
          sum +
          parseDollarsStrToCents(item.amountDollarsStr) *
            Math.max(1, Number(item.quantity) || 1),
        0,
      ),
    [additionalLineItems],
  );

  const discountAmountCents = useMemo(() => {
    if (!effectiveAppliedDiscount) return 0;
    if (
      typeof effectiveAppliedDiscount.computedAmount === "number" &&
      Number.isFinite(effectiveAppliedDiscount.computedAmount)
    ) {
      return Math.max(0, Math.round(effectiveAppliedDiscount.computedAmount));
    }
    const preDiscountSubtotal =
      productSubtotalPreDiscount + additionalLineItemSubtotal;
    return effectiveAppliedDiscount.unit === EnumDiscountUnit.Percentage
      ? Math.round(preDiscountSubtotal * effectiveAppliedDiscount.amount)
      : effectiveAppliedDiscount.unit === EnumDiscountUnit.FlatRate
        ? Math.max(0, preDiscountSubtotal - effectiveAppliedDiscount.amount)
        : Math.min(preDiscountSubtotal, effectiveAppliedDiscount.amount);
  }, [
    effectiveAppliedDiscount,
    productSubtotalPreDiscount,
    additionalLineItemSubtotal,
  ]);

  const invoiceDiscountLabel = useMemo(() => {
    if (!effectiveAppliedDiscount) return "";
    const discountDoc = booker?.discounts?.find(
      d => d._id === effectiveAppliedDiscount.discountId,
    );
    const ticketCount = getPerTicketDiscountCount(
      {
        unit: effectiveAppliedDiscount.unit,
        amount: effectiveAppliedDiscount.amount,
        perTicket: !!discountDoc?.perTicket,
      },
      discountAmountCents,
    );
    const rateLabel =
      effectiveAppliedDiscount.unit === EnumDiscountUnit.Percentage
        ? `${effectiveAppliedDiscount.amount * 100}% off`
        : effectiveAppliedDiscount.unit === EnumDiscountUnit.FlatRate
          ? `$${formatPrice(effectiveAppliedDiscount.amount)} flat rate`
          : `$${formatPrice(effectiveAppliedDiscount.amount)} off`;
    const withTickets =
      ticketCount != null && ticketCount > 0
        ? `${rateLabel} × ${ticketCount}`
        : rateLabel;
    return `${effectiveAppliedDiscount.name} | ${withTickets}`;
  }, [effectiveAppliedDiscount, discountAmountCents, booker?.discounts]);

  // Widget discounts the admin can apply manually. Only offered on
  // renewable-fee widgets: an ongoing discount picked here (e.g. a
  // "Preferred" rate) carries onto the RenewablePurchase when the invoice is
  // paid, so every future renewal bills the discounted rate. Other product
  // types keep using additional line items for one-off tweaks.
  const selectableDiscounts = useMemo(
    () =>
      // Flat rate isn't supported on renewals, the only place this picker shows.
      (booker?.discounts ?? []).filter(
        (d): d is NonNullable<typeof d> =>
          !!d &&
          d.isActive !== false &&
          d.unit !== EnumDiscountUnit.FlatRate,
      ),
    [booker?.discounts],
  );

  const discountCollection = useMemo(
    () =>
      Select.createCollection({
        items: selectableDiscounts.map(d => {
          const rate =
            d.unit === EnumDiscountUnit.Percentage
              ? `${Math.round(d.amount * 100)}% off`
              : `$${formatPrice(d.amount)} off`;
          const parts = [
            getMultilingualString(d.name ?? {}) || "Discount",
            rate,
          ];
          if (d.promoCode) parts.push(d.promoCode);
          if (d.isOngoing) parts.push("ongoing");
          return { label: parts.join(" • "), value: d._id };
        }),
      }),
    [selectableDiscounts],
  );

  const handleSelectDiscount = useCallback(
    (discountId: string) => {
      const doc = selectableDiscounts.find(d => d._id === discountId);
      if (!doc) return;
      setDiscountRemoved(false);
      setAppliedDiscount({
        discountId: doc._id,
        name: getMultilingualString(doc.name ?? {}) || "Discount",
        promoCode: doc.promoCode ?? undefined,
        unit: doc.unit,
        amount: doc.amount,
        // No computedAmount: the rate is recomputed against the invoice's
        // current pre-discount subtotal (client preview + server both do this).
        isOngoing: !!doc.isOngoing,
      });
    },
    [selectableDiscounts, setAppliedDiscount],
  );

  const handleRemoveDiscount = useCallback(() => {
    setAppliedDiscount(null);
    // Also suppress the cart-derived fallback, otherwise removing a discount
    // that originated from the widget cart would immediately resurface it.
    setDiscountRemoved(true);
  }, [setAppliedDiscount]);

  const invoiceDisplayNumber = useMemo(() => {
    const pr = paymentRequest as
      | {
          order?: string | null;
          invoiceNumber?: string | null;
          _id: string;
        }
      | undefined;
    if (!pr) return "";
    return pr.order ?? pr.invoiceNumber ?? `···${pr._id.slice(-8)}`;
  }, [paymentRequest]);

  /** Value copied to clipboard (full id when falling back to Mongo `_id`). */
  const invoiceClipboardValue = useMemo(() => {
    const pr = paymentRequest as
      | {
          order?: string | null;
          invoiceNumber?: string | null;
          _id: string;
        }
      | undefined;
    if (!pr) return "";
    return pr.order ?? pr.invoiceNumber ?? pr._id;
  }, [paymentRequest]);

  const changeHistoryNewestFirst = useMemo(() => {
    const changeHistory = consolidateRecordPaymentHistory(
      paymentRequest?.changeHistory ?? [],
    );
    const syntheticPaymentEntries = buildSyntheticPaymentHistoryEntries(
      paymentRequest?.paymentEntries,
      changeHistory,
    );
    const combined = [...changeHistory, ...syntheticPaymentEntries];
    return combined.length ? sortInvoiceJournalEntries(combined) : [];
  }, [paymentRequest?.changeHistory, paymentRequest?.paymentEntries]);
  const paymentEntriesById = useMemo(
    () =>
      new Map(
        (paymentRequest?.paymentEntries ?? []).map((entry: any) => [
          String(entry._id),
          entry,
        ]),
      ),
    [paymentRequest?.paymentEntries],
  );
  // Legacy SilkStart-imported invoices carry their original SilkStart
  // invoice number as `invoiceNumber` (e.g. "LEGACY-218457") — same shape
  // used for legacy imported orders — so the same proxy route can serve the
  // original PDF here too.
  const legacyInvoicePdfUrl = useMemo(
    () =>
      resolveLegacyReceiptPdfUrl({
        orderId: paymentRequest?.invoiceNumber ?? "",
      }),
    [paymentRequest?.invoiceNumber],
  );
  const selectedOperatorName = useMemo(
    () =>
      selectedOperatorData?.operatorById?.name
        ? getMultilingualString(selectedOperatorData.operatorById.name)
        : "",
    [selectedOperatorData?.operatorById?.name],
  );
  // Falls back to the invoice's booker product (the shared "Invoice"
  // placeholder for blank invoices, or the real widget product otherwise)
  // when no admin override is set.
  const defaultProductName = useMemo(
    () => getMultilingualString(booker?.bundles?.[0]?.product?.name ?? {}),
    [booker?.bundles],
  );
  const effectiveProductName = productNameOverride.trim() || defaultProductName;
  const recipientName = useMemo(() => {
    const fullName =
      `${recipientContact.firstName ?? ""} ${recipientContact.lastName ?? ""}`.trim();

    return fullName || paymentRequest?.recipient?.trim() || "—";
  }, [
    paymentRequest?.recipient,
    recipientContact.firstName,
    recipientContact.lastName,
  ]);

  const combinedSubtotal =
    (isStatement ? statementSubtotal : productSubtotalPreDiscount) +
    additionalLineItemSubtotal;

  // Server-authoritative totals: the composer displays exactly what the
  // backend will persist and charge (same engine as paymentRequestUpdate).
  const priceComputeVariables = useMemo(
    () => ({
      bookerId: booker?._id,
      productSubtotal: productSubtotalPreDiscount,
      additionalLineItems: additionalLineItems
        .filter(
          item =>
            item.description.trim().length > 0 ||
            parseDollarsStrToCents(item.amountDollarsStr) !== 0,
        )
        .map(item => ({
          description: item.description.trim(),
          amount: parseDollarsStrToCents(item.amountDollarsStr),
          quantity: Math.max(1, Number(item.quantity) || 1),
        })),
      appliedDiscount: effectiveAppliedDiscount
        ? {
            unit: effectiveAppliedDiscount.unit,
            amount: effectiveAppliedDiscount.amount,
            ...(typeof effectiveAppliedDiscount.computedAmount === "number"
              ? { discountAmount: effectiveAppliedDiscount.computedAmount }
              : {}),
          }
        : null,
      taxExempt,
      paymentRequestId: isStatement ? paymentRequest?._id : undefined,
    }),
    [
      booker?._id,
      productSubtotalPreDiscount,
      additionalLineItems,
      effectiveAppliedDiscount,
      taxExempt,
      isStatement,
      paymentRequest?._id,
    ],
  );
  const {
    data: priceComputeData,
    previousData: previousPriceComputeData,
    refetch: refetchPriceCompute,
  } = useQuery(PAYMENT_REQUEST_PRICE_COMPUTE, {
    variables: priceComputeVariables,
    skip: !booker?._id,
  });
  const serverTotals =
    priceComputeData?.paymentRequestPriceCompute ??
    previousPriceComputeData?.paymentRequestPriceCompute;

  const displayDiscountAmount =
    serverTotals?.discountAmount ?? discountAmountCents;
  const displayTaxLines: {
    name: string;
    ratePercent?: number | null;
    amount: number;
  }[] = serverTotals?.taxLines ?? paymentRequest?.taxLines ?? [];
  const displayTaxAmount =
    serverTotals?.taxAmount ?? paymentRequest?.taxAmount ?? 0;
  const displayCustomerFee =
    serverTotals?.customerFee ?? paymentRequest?.customerFee ?? 0;
  const displayGrandTotal =
    serverTotals?.grandTotal ?? paymentRequest?.grandTotal ?? 0;

  const isReadOnly = status === "PAID" || status === "CANCELLED";
  // Manual discount picking is only offered on renewable-fee widgets, where
  // an ongoing discount drives the special-rate renewal flow.
  const canPickDiscount =
    isRenewableFee && !isReadOnly && selectableDiscounts.length > 0;
  const canSaveDraft = status === "DRAFT" || status === "FOR_REVIEW";
  const canSaveRevision =
    status === "SENT" ||
    status === "OVERDUE" ||
    (accountingControlsEnabled && status === "PENDING");
  const canFlagNeedsAttention =
    !isReadOnly && status !== "REVISED" && !!activePaymentRequestId;
  const canSend =
    isStorefrontOwner && (status === "DRAFT" || status === "FOR_REVIEW");
  const canSubmitForReview = !isStorefrontOwner && status === "DRAFT";
  const canSettleWithoutSending =
    accountingControlsEnabled &&
    isStorefrontOwner &&
    (status === "DRAFT" || status === "FOR_REVIEW");
  // FOR_REVIEW invoices have never been sent, so deleting one is a clean
  // rollback (renewal invoices also release their renewal-period lock).
  const canDelete = status === "DRAFT" || status === "FOR_REVIEW";
  const canCancel = [
    "DRAFT",
    "FOR_REVIEW",
    "SENT",
    "OVERDUE",
    "PENDING",
  ].includes(status ?? "");
  const canRecordPayment =
    accountingControlsEnabled &&
    ["FOR_REVIEW", "SENT", "OVERDUE", "PENDING", "PAID"].includes(status ?? "");
  const canMarkPaid =
    !accountingControlsEnabled &&
    ["SENT", "OVERDUE", "PENDING"].includes(status ?? "");
  const canResend =
    accountingControlsEnabled &&
    ["SENT", "OVERDUE", "PENDING"].includes(status ?? "");
  const canMarkPending = ["SENT", "OVERDUE"].includes(status ?? "");
  const canUndoPending = status === "PENDING";

  // Courtesy changelog: reactively keep the "Membership option changed…" /
  // "Your renewal date is now…" lines inside the customer notes while the
  // admin edits renewal settings. Each change replaces the previous
  // auto-managed block; reverting to the saved values removes it. After a
  // save the block becomes part of the saved notes (the ref is reset), so it
  // is never stripped retroactively.
  useEffect(() => {
    if (!isRenewalInvoice || isReadOnly) return;

    const lines = buildRenewalSettingsChangelogLines({
      tierChanged:
        selectedTierId && savedTierId && selectedTierId !== savedTierId
          ? {
              fromName: savedRenewalTierName || "Previous option",
              toName: selectedRenewalTierName || "New option",
            }
          : undefined,
      dateChanged:
        renewalDate && savedRenewalDate && renewalDate !== savedRenewalDate
          ? {
              fromDate: savedRenewalDate,
              toDate: renewalDate,
              isFixedDate: !!isFixedDateRenewal,
            }
          : undefined,
    });
    const nextBlock = lines.join("\n");
    const prevBlock = autoChangelogRef.current;
    if (nextBlock === prevBlock) return;

    setNotes(prev => {
      let base = prev;
      if (prevBlock && base.includes(prevBlock)) {
        base = base.replace(prevBlock, "");
      }
      base = base.trim();
      if (!nextBlock) return base;
      return base ? `${base}\n\n${nextBlock}` : nextBlock;
    });
    setNotesHighlighted(!!nextBlock);
    autoChangelogRef.current = nextBlock;
  }, [
    isRenewalInvoice,
    isReadOnly,
    selectedTierId,
    savedTierId,
    selectedRenewalTierName,
    savedRenewalTierName,
    renewalDate,
    savedRenewalDate,
    isFixedDateRenewal,
  ]);

  const buildRecord = (extra?: Record<string, unknown>) => ({
    recipientContact: {
      ...recipientContact,
      ...(customerAddress
        ? { address: addressSearchValueToAddressInput(customerAddress) }
        : {}),
    },
    recipientId: recipientPersonId ?? undefined,
    operatorId: selectedOperatorId ?? undefined,
    // For term selections ("15 days" etc.) the server stores the term and
    // re-stamps the concrete due date at send time; the date sent here is a
    // provisional display value. An empty string intentionally clears any
    // previously saved due date.
    dueDate: isDueDateTermMode
      ? isoDateInDays(dueDateTermDays)
      : isDueDateRenewalMode
        ? renewalDate
        : dueDate,
    dueTermDays: isDueDateTermMode ? dueDateTermDays : null,
    ...(scheduledSendDate ? { scheduledSendDate } : {}),
    cartSnapshot: serializeCart(),
    additionalLineItems: additionalLineItems
      .filter(item => item.description.trim().length > 0)
      .map(item => ({
        description: item.description.trim(),
        amount: parseDollarsStrToCents(item.amountDollarsStr),
        quantity: Math.max(1, Number(item.quantity) || 1),
      })),
    notes: notes.trim() || undefined,
    invoicePolicy: invoicePolicyText.trim() || undefined,
    accountingCode: accountingCode || undefined,
    productNameOverride: productNameOverride.trim() || undefined,
    taxExempt,
    ...(isRenewalInvoice
      ? {
          nextRenewalDate: renewalDate || undefined,
          renewalTierId: selectedTierId || undefined,
        }
      : {}),
    appliedDiscount: effectiveAppliedDiscount
      ? {
          discountId: effectiveAppliedDiscount.discountId,
          name: effectiveAppliedDiscount.name,
          promoCode: effectiveAppliedDiscount.promoCode,
          unit: effectiveAppliedDiscount.unit,
          amount: effectiveAppliedDiscount.amount,
          discountAmount: discountAmountCents,
          isOngoing: isRenewableFee
            ? effectiveAppliedDiscount.isOngoing
            : false,
        }
      : null,
    // Products-only subtotal (pre-discount); the server recomputes all derived
    // totals (discount, tax, fee, grand total) via computeInvoiceTotals.
    productSubtotal: productSubtotalPreDiscount,
    currency: paymentRequest?.currency ?? "cad",
    ...extra,
  });

  const syncPaymentRequest = async (extra?: Record<string, unknown>) => {
    // The courtesy changelog is maintained reactively inside `notes` (see the
    // effect above), so the current notes are already save-ready.
    const notesForSave =
      extra?.notes !== undefined ? String(extra.notes) : notes;

    const response = await updatePaymentRequest({
      variables: {
        paymentRequestId: activePaymentRequestId,
        version,
        record: {
          ...buildRecord(extra),
          notes: notesForSave.trim() || undefined,
        },
      },
    });

    const updated = response.data?.paymentRequestUpdate;
    const nextPaymentRequestId = updated?._id ?? activePaymentRequestId;
    const idChanged = updated?._id && updated._id !== activePaymentRequestId;
    if (idChanged) {
      setActivePaymentRequestId(updated._id);
      hydratedPaymentRequestIdRef.current = null;
      hydratedBillingPaymentRequestIdRef.current = null;
    }
    await refetch({ paymentRequestId: nextPaymentRequestId });
    if (isRenewalInvoice) {
      // The just-saved changelog lines are now part of the persisted notes;
      // clear the auto-managed block so they aren't stripped when the saved
      // baseline values update below.
      autoChangelogRef.current = "";
      setSavedRenewalDate(renewalDate);
      setSavedTierId(selectedTierId ?? "");
    }
    return updated;
  };

  const handleSave = async () => {
    if (canSaveRevision) {
      setRevisionNote("");
      setNotifyRecipientOnRevision(true);
      setRevisionModalOpen(true);
      return;
    }
    try {
      await syncPaymentRequest();
      toastFactory.create({ title: "Draft saved" });
    } catch (mutationError: any) {
      toastFactory.create({
        title: "Could not save invoice",
        description: mutationError?.message,
        type: "error",
      });
    }
  };

  const handleToggleNeedsAttention = async () => {
    if (!activePaymentRequestId) return;
    const nextNeedsAttention = !paymentRequest?.needsAttention;
    try {
      await setNeedsAttention({
        variables: {
          paymentRequestId: activePaymentRequestId,
          needsAttention: nextNeedsAttention,
        },
      });
      await refetch({ paymentRequestId: activePaymentRequestId });
      toastFactory.create({
        title: nextNeedsAttention
          ? "Flagged as needs attention"
          : "Needs attention flag removed",
      });
    } catch (mutationError: any) {
      toastFactory.create({
        title: "Could not update needs attention flag",
        description: mutationError?.message,
        type: "error",
      });
    }
  };

  const handleSaveInternalNote = async () => {
    if (!activePaymentRequestId) return;
    const note = internalNoteDraft.trim();
    if (!note) {
      toastFactory.create({
        title: "Note is required",
        type: "error",
      });
      return;
    }
    try {
      await addInternalNote({
        variables: {
          paymentRequestId: activePaymentRequestId,
          note,
        },
      });
      setInternalNoteDraft("");
      setIsInternalNoteEditorOpen(false);
      await refetch({ paymentRequestId: activePaymentRequestId });
      toastFactory.create({ title: "Internal note added" });
    } catch (mutationError: any) {
      toastFactory.create({
        title: "Could not add internal note",
        description: mutationError?.message,
        type: "error",
      });
    }
  };

  const confirmSaveRevision = async () => {
    try {
      const updated = await syncPaymentRequest({
        revisionNote: revisionNote.trim() || undefined,
        notifyRecipient: notifyRecipientOnRevision,
      });
      setRevisionModalOpen(false);
      await refetch({
        paymentRequestId: updated?._id ?? activePaymentRequestId,
      });
      toastFactory.create({
        title: "Revision saved",
        description: notifyRecipientOnRevision
          ? "The payer was emailed an updated invoice."
          : undefined,
      });
    } catch (mutationError: any) {
      toastFactory.create({
        title: "Could not save revision",
        description: mutationError?.message,
        type: "error",
      });
    }
  };

  const handleSend = async () => {
    if (!recipientContact.email?.trim()) {
      const message = "Add a recipient email to send.";
      setSendError(message);
      toastFactory.create({
        title: "Recipient required",
        description: message,
        type: "error",
      });
      return;
    }
    setSendError(null);
    try {
      await syncPaymentRequest();
      const latest = await refetch();
      const p = latest.data?.paymentRequestById;
      if (!p?._id) throw new Error("Missing payment request after save");
      await sendPaymentRequest({
        variables: { paymentRequestId: p._id, version: p.version },
      });
      await refetch({ paymentRequestId: p._id });
      toastFactory.create({ title: "Invoice sent" });
    } catch (mutationError: any) {
      toastFactory.create({
        title: "Could not send invoice",
        description: mutationError?.message,
        type: "error",
      });
    }
  };

  const handleOpenSettle = async () => {
    if (!recipientContact.email?.trim()) {
      const message = "Add a recipient email to settle this invoice.";
      setSendError(message);
      toastFactory.create({
        title: "Recipient required",
        description: message,
        type: "error",
      });
      return;
    }
    setSendError(null);
    setIsPreparingSettle(true);
    try {
      await syncPaymentRequest();
      setSettleModalOpen(true);
    } catch (mutationError: any) {
      toastFactory.create({
        title: "Could not save invoice",
        description: mutationError?.message,
        type: "error",
      });
    } finally {
      setIsPreparingSettle(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deletePaymentRequest({
        variables: { paymentRequestId: activePaymentRequestId },
      });
      toastFactory.create({ title: "Invoice deleted" });
      onCloseBookerModal();
    } catch (mutationError: any) {
      toastFactory.create({
        title: "Could not delete invoice",
        description: mutationError?.message,
        type: "error",
      });
    }
  };

  const handleCancel = async () => {
    if (!cancellationNote.trim()) {
      toastFactory.create({
        title: "Cancellation note required",
        description: "Add a reason before cancelling this invoice.",
        type: "error",
      });
      return;
    }
    try {
      await cancelPaymentRequest({
        variables: {
          paymentRequestId: activePaymentRequestId,
          version,
          cancellationNote: cancellationNote.trim(),
        },
      });
      setCancelModalOpen(false);
      await refetch();
      toastFactory.create({ title: "Invoice cancelled" });
    } catch (mutationError: any) {
      toastFactory.create({
        title: "Could not cancel invoice",
        description: mutationError?.message,
        type: "error",
      });
    }
  };

  const handleRemoveStatementOrder = async (orderId: string) => {
    if (!paymentRequest?._id) return;
    setRemovingStatementOrderId(orderId);
    try {
      await removeStatementOrder({
        variables: { paymentRequestId: paymentRequest._id, orderId },
      });
      await Promise.all([refetch(), refetchPriceCompute()]);
      toastFactory.create({
        title: "Booking removed from statement",
        description: "It will be picked up by the next statement run.",
      });
    } catch (mutationError: any) {
      toastFactory.create({
        title: "Could not remove booking",
        description: mutationError?.message,
        type: "error",
      });
    } finally {
      setRemovingStatementOrderId(null);
    }
  };

  const handleResend = async () => {
    try {
      await resendPaymentRequest({
        variables: {
          paymentRequestId: activePaymentRequestId,
          version,
          note: resendNote.trim() || undefined,
          idempotencyKey: resendIdempotencyKeyRef.current,
        },
      });
      setResendModalOpen(false);
      setResendNote("");
      resendIdempotencyKeyRef.current = crypto.randomUUID();
      await refetch();
      toastFactory.create({ title: "Invoice resent" });
    } catch (mutationError: any) {
      toastFactory.create({
        title: "Could not resend invoice",
        description: mutationError?.message,
        type: "error",
      });
    }
  };

  const handleLegacyMarkPaid = async () => {
    try {
      await markPaymentRequestPaid({
        variables: {
          paymentRequestId: activePaymentRequestId,
          version,
          note: legacyMarkPaidNote.trim() || undefined,
        },
      });
      setLegacyMarkPaidModalOpen(false);
      setLegacyMarkPaidNote("");
      await refetch();
      toastFactory.create({ title: "Invoice marked as paid" });
    } catch (mutationError: any) {
      toastFactory.create({
        title: "Could not mark invoice as paid",
        description: mutationError?.message,
        type: "error",
      });
    }
  };

  const handleSubmitForReview = async () => {
    try {
      await syncPaymentRequest();
      const latest = await refetch();
      const p = latest.data?.paymentRequestById;
      if (!p?._id) throw new Error("Missing payment request after save");
      await submitForReview({
        variables: {
          paymentRequestId: p._id,
          version: p.version,
        },
      });
      await refetch();
      toastFactory.create({ title: t("SUBMIT_FOR_REVIEW_SUCCESS") });
    } catch (mutationError: any) {
      toastFactory.create({
        title: "Could not submit invoice for review",
        description: mutationError?.message,
        type: "error",
      });
    }
  };

  const handleMarkPending = async () => {
    try {
      await markPending({
        variables: {
          paymentRequestId: activePaymentRequestId,
          version,
        },
      });
      await refetch();
      toastFactory.create({ title: t("MARK_PENDING_SUCCESS") });
    } catch (mutationError: any) {
      toastFactory.create({
        title: "Could not mark invoice as pending",
        description: mutationError?.message,
        type: "error",
      });
    }
  };

  const handleUndoPending = async () => {
    try {
      await undoPending({
        variables: {
          paymentRequestId: activePaymentRequestId,
          version,
        },
      });
      setUndoPendingModalOpen(false);
      await refetch();
      toastFactory.create({ title: "Invoice returned to sent" });
    } catch (mutationError: any) {
      toastFactory.create({
        title: "Could not undo pending status",
        description: mutationError?.message,
        type: "error",
      });
    }
  };

  const handleOpenInvoiceNumberModal = () => {
    if (canSaveDraft) {
      setInvoiceNumberInput(paymentRequest?.invoiceNumber ?? "");
      setInvoiceNumberError(null);
      setEditInvoiceNumberModalOpen(true);
    } else {
      setInvoiceNumberLockedModalOpen(true);
    }
  };

  const handleInvoiceNumberInputChange = (raw: string) => {
    setInvoiceNumberInput(
      raw
        .toUpperCase()
        .replace(/[^0-9A-Z\-_.]/g, "")
        .slice(0, 30),
    );
    setInvoiceNumberError(null);
  };

  const handleSaveInvoiceNumber = async () => {
    const trimmed = invoiceNumberInput.trim();
    if (!trimmed) {
      setInvoiceNumberError("Invoice number is required.");
      return;
    }
    if (trimmed.length > 30) {
      setInvoiceNumberError("Invoice number must be 30 characters or fewer.");
      return;
    }
    if (/^\.+$/.test(trimmed)) {
      setInvoiceNumberError("Invoice number cannot consist of only periods.");
      return;
    }
    try {
      const response = await updateInvoiceNumber({
        variables: {
          paymentRequestId: activePaymentRequestId,
          version,
          record: { invoiceNumber: trimmed },
        },
      });
      const updated = response.data?.paymentRequestUpdate;
      await refetch({
        paymentRequestId: updated?._id ?? activePaymentRequestId,
      });
      setEditInvoiceNumberModalOpen(false);
      toastFactory.create({ title: t("EDIT_INVOICE_NUMBER_SUCCESS") });
    } catch (mutationError: any) {
      setInvoiceNumberError(
        mutationError?.message ?? "Could not update invoice number",
      );
    }
  };

  if (loading) {
    return (
      <Flex justifyContent="center" py="600">
        <Spinner width="48px" height="48px" />
      </Flex>
    );
  }

  if (error || !paymentRequest) {
    return (
      <Text visual="smallMedium" color="text.error.dark">
        This invoice could not be loaded.
      </Text>
    );
  }

  const visibleLineItems = additionalLineItems.filter(
    item => item.description.trim().length > 0,
  );

  return (
    <Flex flexDir="column" gap="400" pb="400">
      <Flex flexDir="column" gap="200" w="full">
        <Flex
          alignItems="center"
          justifyContent="space-between"
          w="full"
          gap="150"
          flexWrap="wrap"
        >
          <Flex alignItems="center" gap="100" flexShrink={0}>
            <Icon Element={Cart} size="icons.lg" />
            <Heading size="h5">{t("PAYMENT")}</Heading>
          </Flex>
          <Tag visual={tag.visual} mode={tag.mode} flexShrink={0}>
            {tag.label}
          </Tag>
        </Flex>
        {/* Each field is a label+value unit with a tight internal gap ("50");
            the parent Flex's gap ("200") provides the consistent separation
            between fields. */}
        <Flex flexDir="column" gap="50" w="full">
          <Text visual="smallMedium" color="text.light">
            {t("INVOICE_NUMBER")}
          </Text>
          <Flex alignItems="center" gap="50">
            <Text
              fontFamily="mono"
              fontSize="2xs"
              lineHeight="2xs"
              fontWeight="medium!"
              color="text.dark"
            >
              {invoiceDisplayNumber}
            </Text>
            <Button
              visual="ghost"
              size="sm"
              p="4px"
              aria-label={t("EDIT_INVOICE_NUMBER")}
              onClick={handleOpenInvoiceNumberModal}
            >
              <Icon Element={EditPencil} size="sm" color="icon.mid!" />
            </Button>
            {["SENT", "OVERDUE"].includes(status ?? "") &&
            paymentRequest?.invoiceNumber ? (
              <Button
                visual="ghost"
                size="sm"
                p="4px"
                aria-label={t("OPEN_PAY_PAGE")}
                onClick={() => {
                  window.open(
                    `${window.location.origin}/invoice/${paymentRequest.invoiceNumber}`,
                    "_blank",
                    "noopener,noreferrer",
                  );
                }}
              >
                <Icon Element={OpenNewWindow} size="sm" color="icon.mid!" />
              </Button>
            ) : null}
            {invoiceClipboardValue ? (
              <CopyToClipboard
                text={invoiceClipboardValue}
                iconOnly
                iconSize="sm"
                iconCopyColor="icon.mid"
                buttonProps={{
                  p: "4px",
                  "aria-label": t("COPY_INVOICE_NUMBER"),
                }}
              />
            ) : null}
          </Flex>
        </Flex>
        <Flex flexDir="column" gap="50">
          <Text visual="smallMedium" color="text.light">
            Recipient
          </Text>
          <Text visual="smallRegular" color="text.dark">
            {recipientName}
          </Text>
        </Flex>
        <Flex flexDir="column" gap="50">
          <Text visual="smallMedium" color="text.light">
            Recipient email
          </Text>
          <Text visual="smallRegular" color="text.dark">
            {recipientContact.email || "—"}
          </Text>
        </Flex>
        {selectedOperatorName ? (
          <Flex flexDir="column" gap="50">
            <Text visual="smallMedium" color="text.light">
              Operator
            </Text>
            <Text visual="smallRegular" color="text.dark">
              {selectedOperatorName}
            </Text>
          </Flex>
        ) : null}
        {!selectedOperatorName && businessField?.businessName ? (
          <Flex flexDir="column" gap="50">
            <Text visual="smallMedium" color="text.light">
              Business
            </Text>
            <Text visual="smallRegular" color="text.dark">
              {businessField.businessName}
            </Text>
          </Flex>
        ) : null}
        <Flex flexDir="column" gap="50" maxW={{ base: "full", md: "260px" }}>
          <Text visual="smallMedium" color="text.light">
            {t("PRODUCT_NAME")}
          </Text>
          {isEditingProductName ? (
            <Flex flexDir="column" gap="100">
              {customProductNameOptions.length > 0 ? (
                <Select.Root
                  placeholder={t("PRODUCT_NAME_PRESET_PLACEHOLDER")}
                  collection={customProductNameCollection}
                  // Acts as a picker: the chosen preset fills the input below
                  // (where it can still be edited), so no value is retained.
                  value={[]}
                  onValueChange={({ value }: { value: string[] }) => {
                    if (value[0]) setProductNameDraft(value[0]);
                  }}
                >
                  {customProductNameCollection.items.map(item => (
                    <Select.Item key={item.value} item={item}>
                      {item.label}
                    </Select.Item>
                  ))}
                </Select.Root>
              ) : null}
              <Flex alignItems="center" gap="100">
                <Input
                  value={productNameDraft}
                  onChange={e => setProductNameDraft(e.target.value)}
                  placeholder={defaultProductName}
                  autoFocus
                  onKeyDown={e => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      setProductNameOverride(productNameDraft.trim());
                      setIsEditingProductName(false);
                    } else if (e.key === "Escape") {
                      e.preventDefault();
                      setIsEditingProductName(false);
                    }
                  }}
                />
                <Button
                  visual="ghost"
                  size="sm"
                  p="4px"
                  aria-label={t("SAVE_PRODUCT_NAME")}
                  onClick={() => {
                    setProductNameOverride(productNameDraft.trim());
                    setIsEditingProductName(false);
                  }}
                >
                  <Icon Element={Check} size="sm" color="icon.mid!" />
                </Button>
                <Button
                  visual="ghost"
                  size="sm"
                  p="4px"
                  aria-label={t("CANCEL_PRODUCT_NAME")}
                  onClick={() => setIsEditingProductName(false)}
                >
                  <Icon Element={Xmark} size="sm" color="icon.mid!" />
                </Button>
              </Flex>
            </Flex>
          ) : (
            <Flex alignItems="center" gap="100">
              <Text visual="smallRegular" color="text.dark">
                {effectiveProductName || "—"}
              </Text>
              {!isReadOnly ? (
                <Button
                  visual="ghost"
                  size="sm"
                  p="4px"
                  aria-label={t("EDIT_PRODUCT_NAME")}
                  onClick={() => {
                    setProductNameDraft(effectiveProductName);
                    setIsEditingProductName(true);
                  }}
                >
                  <Icon Element={EditPencil} size="sm" color="icon.mid!" />
                </Button>
              ) : null}
            </Flex>
          )}
        </Flex>
        {isRenewalInvoice ? (
          <>
            <Flex flexDir="column" gap="50">
              <Text visual="smallMedium" color="text.light">
                {t("RENEWAL_DATE")}
              </Text>
              <DateInput
                value={renewalDate || undefined}
                onValueChange={setRenewalDate}
                fullWidth
                containerProps={{ maxW: { base: "full", md: "260px" } }}
                triggerProps={{
                  disabled: isReadOnly,
                  width: "full!",
                  minWidth: "auto!",
                  justifyContent: "space-between!",
                  px: "150!",
                  py: "100!",
                }}
              />
            </Flex>
            <Flex flexDir="column" gap="50">
              <Text visual="smallMedium" color="text.light">
                {t("RENEWAL_PERIOD")}
              </Text>
              {previewRenewalPeriodLabel ? (
                <Flex flexDir="column" gap="25">
                  <Text
                    visual="smallRegular"
                    color="text.light"
                    textDecoration="line-through"
                  >
                    {renewalPeriodLabel}
                  </Text>
                  <Text visual="smallRegular" color="text.dark">
                    {previewRenewalPeriodLabel}
                  </Text>
                </Flex>
              ) : (
                <Text visual="smallRegular" color="text.dark">
                  {renewalPeriodLabel}
                </Text>
              )}
            </Flex>
          </>
        ) : null}
        <Flex flexDir="column" gap="50" maxW={{ base: "full", md: "260px" }}>
          <Text visual="smallMedium" color="text.light">
            {t("DUE_DATE")}
          </Text>
          <Select.Root
            collection={dueDateModeCollection}
            multiple={false}
            value={[dueDateMode]}
            onValueChange={({ value }) => {
              const mode = value[0] ?? DUE_DATE_CUSTOM_VALUE;
              setDueDateMode(mode);
              if (mode === DUE_DATE_NONE_VALUE) {
                setDueDate("");
                setAutoOpenCustomDueDate(false);
              } else if (mode === DUE_DATE_CUSTOM_VALUE) {
                setAutoOpenCustomDueDate(true);
              } else if (mode === DUE_DATE_RENEWAL_VALUE) {
                setDueDate(renewalDate);
                setAutoOpenCustomDueDate(false);
              } else {
                setDueDate(isoDateInDays(Number(mode)));
                setAutoOpenCustomDueDate(false);
              }
            }}
            disabled={isReadOnly}
          >
            {dueDateModeCollection.items.map(item => (
              <Select.Item key={item.value} item={item}>
                {item.label}
              </Select.Item>
            ))}
          </Select.Root>
          {dueDateMode === DUE_DATE_CUSTOM_VALUE ? (
            <DateInput
              value={dueDate || undefined}
              onValueChange={setDueDate}
              canSelectPastDates
              fullWidth
              autoOpen={autoOpenCustomDueDate}
              triggerProps={{
                disabled: isReadOnly,
                width: "full!",
                minWidth: "auto!",
                justifyContent: "space-between!",
                px: "150!",
                py: "100!",
              }}
            />
          ) : null}
          {isDueDateTermMode ? (
            <Text visual="smallRegular" color="text.mid">
              {t("DUE_DATE_ESTIMATE", {
                date: formatDate(isoDateInDays(dueDateTermDays), "MMM dd yyyy"),
              })}
            </Text>
          ) : null}
          {isDueDateRenewalMode && renewalDate ? (
            <Text visual="smallRegular" color="text.mid">
              {t("DUE_DATE_RENEWAL_HINT", {
                date: formatDate(renewalDate, "MMM dd yyyy"),
              })}
            </Text>
          ) : null}
          {/* "Email send date" scheduling UI is hidden for now; existing
              scheduledSendDate values are still preserved on save. */}
        </Flex>
      </Flex>

      {/* Discount — widget/cart discounts flow in automatically. On
          renewable-fee widgets the admin can also apply one of the widget's
          discounts manually: an ongoing discount (e.g. a "Preferred" rate)
          carries onto the purchase when the invoice is paid, so all future
          renewals bill the discounted rate. Other product types keep the
          read-only summary; use additional line items for tweaks. */}
      {(effectiveAppliedDiscount || canPickDiscount) && (
        <Flex flexDir="column" gap="200">
          <Text visual="bodySemiBold">Discount</Text>
          {effectiveAppliedDiscount ? (
            <Flex
              gap="150"
              p="200"
              borderWidth="1px"
              borderColor="border.light"
              borderRadius="md"
              alignItems="center"
            >
              <Flex flexDir="column" gap="50" flex="1" minW="0">
                <Flex alignItems="center" gap="100" flexWrap="wrap">
                  <Text visual="smallSemiBold">
                    {effectiveAppliedDiscount.name}
                  </Text>
                  {isRenewableFee && effectiveAppliedDiscount.isOngoing && (
                    <Tag visual="success" mode="light" size="sm">
                      Ongoing — renews at this rate
                    </Tag>
                  )}
                </Flex>
                <Text visual="smallRegular" color="text.mid">
                  {invoiceDiscountLabel.replace(
                    `${effectiveAppliedDiscount.name} | `,
                    "",
                  )}
                  {effectiveAppliedDiscount.promoCode
                    ? ` • ${effectiveAppliedDiscount.promoCode}`
                    : ""}
                </Text>
              </Flex>
              {canPickDiscount && (
                <Button
                  visual="ghost"
                  size="sm"
                  p="4px"
                  aria-label="Remove discount"
                  onClick={handleRemoveDiscount}
                >
                  <Icon Element={Xmark} size="sm" color="icon.mid!" />
                </Button>
              )}
            </Flex>
          ) : (
            <Select.Root
              placeholder="Apply a widget discount"
              collection={discountCollection}
              multiple={false}
              value={[]}
              onValueChange={({ value }: { value: string[] }) => {
                if (value[0]) handleSelectDiscount(value[0]);
              }}
            >
              {discountCollection.items.map(item => (
                <Select.Item key={item.value} item={item}>
                  {item.label}
                </Select.Item>
              ))}
            </Select.Root>
          )}
        </Flex>
      )}

      {isStatement && (
        <Flex flexDir="column" gap="150">
          <Text visual="bodySemiBold">
            {`Bookings on this statement${
              paymentRequest?.statementPeriod
                ? ` (${paymentRequest.statementPeriod})`
                : ""
            }`}
          </Text>
          {statementLines.length === 0 ? (
            <Text color="text.mid">No bookings left on this statement.</Text>
          ) : (
            <Flex flexDir="column" gap="100">
              {statementLines.map(line => (
                <Flex
                  key={line.orderId}
                  justifyContent="space-between"
                  alignItems="center"
                  gap="150"
                >
                  <Flex flexDir="column" minW={0}>
                    <Text visual="smallMedium" fontFamily="mono">
                      {line.orderId}
                    </Text>
                    {line.serviceDate && (
                      <Text visual="smallRegular" color="text.mid">
                        {`Ended ${formatInTimeZone(
                          line.serviceDate,
                          "UTC",
                          "MMM d, yyyy",
                        )}`}
                      </Text>
                    )}
                  </Flex>
                  <Flex alignItems="center" gap="150" flexShrink={0}>
                    <Text visual="smallMedium">
                      {`$${formatPrice(line.grandTotal)}`}
                    </Text>
                    {(status === "DRAFT" || status === "FOR_REVIEW") && (
                      <Button
                        size="sm"
                        mode="light"
                        onClick={() =>
                          void handleRemoveStatementOrder(line.orderId)
                        }
                        isLoading={
                          isRemovingStatementOrder &&
                          removingStatementOrderId === line.orderId
                        }
                        disabled={isRemovingStatementOrder}
                      >
                        Remove
                      </Button>
                    )}
                  </Flex>
                </Flex>
              ))}
            </Flex>
          )}
          <Text visual="smallRegular" color="text.light">
            Each booking is billed at the amount it was booked for, including
            its tax and fees. Removed bookings go on the next statement.
          </Text>
        </Flex>
      )}

      {/* Additional line items */}
      <Flex flexDir="column" gap="200">
        <Flex
          justifyContent="space-between"
          alignItems="center"
          gap="150"
          flexWrap="wrap"
        >
          <Text visual="bodySemiBold">Additional line items</Text>
          {!isReadOnly && (
            <Flex gap="100" alignItems="center">
              <Button
                size="sm"
                mode="light"
                Icon={PlusCircle}
                onClick={() =>
                  setAdditionalLineItems(prev => [
                    ...prev,
                    { ...EMPTY_LINE_ITEM },
                  ])
                }
                css={{ height: "32px!" }}
              >
                Add line item
              </Button>
            </Flex>
          )}
        </Flex>

        {additionalLineItems.length === 0 ? (
          <Text color="text.mid">No additional line items yet.</Text>
        ) : (
          <Flex flexDir="column" gap="100">
            {/* Column headers — shown once above all rows */}
            <Grid
              gridTemplateColumns={{
                base: "1fr",
                md: isReadOnly
                  ? "minmax(0, 2fr) 1fr 60px"
                  : "minmax(0, 2fr) 1fr 60px auto",
              }}
              gap="150"
            >
              <Text visual="smallMedium" color="text.mid">
                Description
              </Text>
              <Text visual="smallMedium" color="text.mid">
                Amount
              </Text>
              <Text visual="smallMedium" color="text.mid">
                Qty
              </Text>
              {!isReadOnly && <Box />}
            </Grid>
            <Flex flexDir="column" gap="100">
              {additionalLineItems.map((item, index) => (
                <Grid
                  key={`${paymentRequest._id}-line-item-${index}`}
                  gridTemplateColumns={{
                    base: "1fr",
                    md: isReadOnly
                      ? "minmax(0, 2fr) 1fr 60px"
                      : "minmax(0, 2fr) 1fr 60px auto",
                  }}
                  gap="150"
                  alignItems="center"
                >
                  <Input
                    value={item.description}
                    onChange={e =>
                      setAdditionalLineItems(prev =>
                        prev.map((existing, existingIndex) =>
                          existingIndex === index
                            ? { ...existing, description: e.target.value }
                            : existing,
                        ),
                      )
                    }
                    disabled={isReadOnly}
                  />
                  <Input
                    value={item.amountDollarsStr}
                    onChange={e => {
                      const next = sanitizeAmountDollarsInput(e.target.value);
                      setAdditionalLineItems(prev =>
                        prev.map((existing, existingIndex) =>
                          existingIndex === index
                            ? { ...existing, amountDollarsStr: next }
                            : existing,
                        ),
                      );
                    }}
                    inputMode="decimal"
                    disabled={isReadOnly}
                  />
                  <Input
                    value={item.quantity}
                    onChange={e =>
                      setAdditionalLineItems(prev =>
                        prev.map((existing, existingIndex) =>
                          existingIndex === index
                            ? {
                                ...existing,
                                quantity: Math.max(
                                  1,
                                  Number.parseInt(e.target.value || "1", 10),
                                ),
                              }
                            : existing,
                        ),
                      )
                    }
                    type="number"
                    min="1"
                    disabled={isReadOnly}
                  />
                  {!isReadOnly && (
                    <Button
                      mode="light"
                      Icon={Trash}
                      p="8px"
                      alignSelf="center"
                      css={{
                        "& > svg": { width: "icons.sm", height: "icons.sm" },
                      }}
                      onClick={() =>
                        setAdditionalLineItems(prev =>
                          prev.filter(
                            (_, existingIndex) => existingIndex !== index,
                          ),
                        )
                      }
                    />
                  )}
                </Grid>
              ))}
            </Flex>
          </Flex>
        )}
      </Flex>

      {/* Invoice policy */}
      <Flex flexDir="column" gap="100">
        <Flex
          alignItems="center"
          justifyContent="space-between"
          gap="150"
          flexWrap="wrap"
        >
          <Text visual="bodySemiBold" color="text.dark">
            {t("POLICY_LABEL")}
          </Text>
          {policyTemplateCollection.items.length > 0 && (
            <Select.Root
              placeholder={t("POLICY_INSERT_PLACEHOLDER")}
              collection={policyTemplateCollection}
              multiple={false}
              value={[]}
              onValueChange={({ value }) => {
                const selectedPolicy = policyOptions.find(
                  policy => policy?._id === value[0],
                );
                if (selectedPolicy) {
                  setInvoicePolicyText(
                    getMultilingualString(selectedPolicy.content ?? {}),
                  );
                }
              }}
              disabled={isReadOnly}
              width="200px"
              size="sm"
              css={{ height: "32px!" }}
            >
              {policyTemplateCollection.items.map(item => (
                <Select.Item key={item.value} item={item}>
                  {item.label}
                </Select.Item>
              ))}
            </Select.Root>
          )}
        </Flex>
        <Textarea
          value={invoicePolicyText}
          onChange={e => setInvoicePolicyText(e.target.value)}
          rows={3}
          placeholder={t("POLICY_PLACEHOLDER")}
          disabled={isReadOnly}
        />
        {isInvoicePolicyLengthy && (
          <Flex alignItems="center" gap="100">
            <Icon
              Element={WarningTriangle}
              color="icon.warning.mid"
              size="sm"
            />
            <Text visual="smallRegular" color="text.warning.mid">
              {t("POLICY_LENGTH_WARNING")}
            </Text>
          </Flex>
        )}
      </Flex>

      {/* Notes for customer */}
      <Flex flexDir="column" gap="100">
        <Text visual="bodySemiBold" color="text.dark">
          Notes for customer
        </Text>
        <Textarea
          value={notes}
          onChange={e => {
            setNotes(e.target.value);
            if (notesHighlighted) setNotesHighlighted(false);
          }}
          rows={3}
          placeholder="Add a note to include on this invoice (optional)"
          disabled={isReadOnly}
          css={
            notesHighlighted
              ? {
                  borderColor: "var(--colors-border-success-mid)",
                  backgroundColor: "var(--colors-surface-success-light)",
                }
              : undefined
          }
        />
      </Flex>

      {/* Accounting code */}
      <Flex flexDir="column" gap="100" maxW={{ base: "full", md: "260px" }}>
        <Text visual="bodySemiBold" color="text.dark">
          {t("ACCOUNTING_CODE")}
        </Text>
        <Select.Root
          placeholder={t("ACCOUNTING_CODE_PLACEHOLDER")}
          collection={accountingCodeCollection}
          multiple={false}
          value={[accountingCode || ACCOUNTING_CODE_NONE_VALUE]}
          onValueChange={({ value }) =>
            setAccountingCode(
              value[0] === ACCOUNTING_CODE_NONE_VALUE ? "" : (value[0] ?? ""),
            )
          }
          disabled={isReadOnly}
        >
          {accountingCodeCollection.items.map(item => (
            <Select.Item key={item.value} item={item}>
              {item.label}
            </Select.Item>
          ))}
        </Select.Root>
      </Flex>

      {/* Tax exempt — removes all tax lines from this invoice */}
      <Flex alignItems="center" gap="150">
        <Switch
          checked={taxExempt}
          onCheckedChange={e => setTaxExempt(!!e?.checked)}
          disabled={isReadOnly}
        />
        <Text visual="smallMedium" color="text.dark">
          {t("TAX_EXEMPT")}
        </Text>
      </Flex>

      {/* Totals — server-computed via paymentRequestPriceCompute */}
      <Box
        borderWidth="1px"
        borderColor="border.light"
        borderRadius="md"
        p="300"
      >
        <PricingSummary
          visual="panel"
          currency={paymentRequest?.currency}
          items={[
            ...(!booker?.isInvoiceTemplate
              ? [
                  {
                    label: "Product subtotal",
                    amount: productSubtotalPreDiscount,
                    detail: summaryMembershipPeriod ?? undefined,
                  },
                ]
              : []),
            ...(isStatement
              ? statementLines.map(line => ({
                  label: `Booking ${line.orderId}`,
                  amount: line.subTotal,
                }))
              : []),
            ...visibleLineItems.map(item => ({
              label: `${item.description}${item.quantity > 1 ? ` ×${item.quantity}` : ""}`,
              amount:
                parseDollarsStrToCents(item.amountDollarsStr) *
                Math.max(1, Number(item.quantity) || 1),
            })),
          ]}
          subtotal={combinedSubtotal}
          discount={
            effectiveAppliedDiscount
              ? {
                  label: invoiceDiscountLabel,
                  amount: displayDiscountAmount,
                }
              : null
          }
          taxLines={displayTaxLines}
          taxTotal={displayTaxAmount}
          customerFee={displayCustomerFee}
          total={displayGrandTotal}
          labels={{
            subtotal: "Subtotal",
            tax: "Tax",
            fee: "Service fee",
            total: "Grand total",
          }}
        />
      </Box>

      {/* Cancellation note — only shown when cancelled */}
      {status === "CANCELLED" && (
        <Flex flexDir="column" gap="100">
          <Text visual="smallSemiBold">Cancellation note</Text>
          <Text visual="smallRegular" color="text.mid">
            {paymentRequest.cancellationNote || "No note provided."}
          </Text>
        </Flex>
      )}

      {/* Action buttons: overflow menu + save icon (left), Send CTA (right) */}
      {!isReadOnly && (
        <Flex flexDir="column" gap="100">
          <Flex justifyContent="space-between" alignItems="center" gap="150">
            <Flex gap="100" alignItems="center">
              <Menu.Root
                trigger={
                  <Button
                    mode="light"
                    Icon={MoreHoriz}
                    p="8px"
                    css={{
                      "& > svg": { width: "icons.sm", height: "icons.sm" },
                    }}
                  />
                }
                positioning={{ placement: "top", offset: { crossAxis: 20 } }}
                onSelect={(item: { value: string }) => {
                  if (item.value === "recordPayment") {
                    setRecordPaymentModalOpen(true);
                  } else if (item.value === "settleWithoutSending") {
                    void handleOpenSettle();
                  } else if (item.value === "markPaid") {
                    setLegacyMarkPaidModalOpen(true);
                  } else if (item.value === "resend") {
                    setResendModalOpen(true);
                  } else if (item.value === "journal") {
                    setJournalModalOpen(true);
                  } else if (item.value === "markPending") {
                    void handleMarkPending();
                  } else if (item.value === "undoPending") {
                    setUndoPendingModalOpen(true);
                  } else if (item.value === "cancel") {
                    setCancellationNote("");
                    setCancelModalOpen(true);
                  } else if (item.value === "delete") {
                    void handleDelete();
                  }
                }}
              >
                {canSettleWithoutSending && (
                  <Menu.Item
                    value="settleWithoutSending"
                    disabled={isPreparingSettle || isSaving || isSending}
                  >
                    Comp or settle without sending
                  </Menu.Item>
                )}
                {canRecordPayment && (
                  <Menu.Item value="recordPayment">Record payment</Menu.Item>
                )}
                {canMarkPaid && (
                  <Menu.Item value="markPaid" disabled={isMarkingPaid}>
                    {t("MARK_PAID")}
                  </Menu.Item>
                )}
                {canResend && (
                  <Menu.Item value="resend" disabled={isResending}>
                    Resend invoice
                  </Menu.Item>
                )}
                {canMarkPending && (
                  <Menu.Item value="markPending" disabled={isMarkingPending}>
                    {t("MARK_PENDING")}
                  </Menu.Item>
                )}
                {canUndoPending && (
                  <Menu.Item value="undoPending" disabled={isUndoingPending}>
                    Undo pending status
                  </Menu.Item>
                )}
                {canCancel && (
                  <Menu.Item value="cancel">Cancel invoice</Menu.Item>
                )}
                {canDelete && (
                  <Menu.Item value="delete">Delete invoice</Menu.Item>
                )}
                {accountingControlsEnabled && (
                  <Menu.Item value="journal">View journal</Menu.Item>
                )}
              </Menu.Root>

              {(canSaveDraft || canSaveRevision) && canFlagNeedsAttention ? (
                <Button
                  mode="light"
                  p="8px"
                  aria-label={
                    paymentRequest?.needsAttention
                      ? "Remove needs attention flag"
                      : "Flag as needs attention"
                  }
                  isLoading={isSettingNeedsAttention}
                  onClick={() => void handleToggleNeedsAttention()}
                >
                  <Icon
                    Element={Star}
                    size="sm"
                    color={
                      paymentRequest?.needsAttention
                        ? "icon.highlight.mid!"
                        : "icon.mid!"
                    }
                  />
                </Button>
              ) : null}

              {(canSaveDraft || canSaveRevision) && (
                <Button
                  mode="light"
                  Icon={FloppyDisk}
                  isLoading={isSaving}
                  onClick={() => void handleSave()}
                  p="8px"
                  css={{ "& > svg": { width: "icons.sm", height: "icons.sm" } }}
                />
              )}
            </Flex>

            {canSend && (
              <Button
                Icon={Send}
                isLoading={isSaving || isSending}
                disabled={isPreparingSettle}
                onClick={() => void handleSend()}
              >
                {t("SEND_INVOICE")}
              </Button>
            )}

            {/* "Schedule send" is hidden along with the send-date input. */}

            {canSubmitForReview && (
              <Button
                Icon={Send}
                isLoading={isSaving || isSubmitting}
                onClick={() => void handleSubmitForReview()}
              >
                {t("SUBMIT_INVOICE")}
              </Button>
            )}

            {canSaveRevision && (
              <Button
                Icon={Send}
                isLoading={isSaving}
                onClick={() => void handleSave()}
              >
                Save revision
              </Button>
            )}
          </Flex>
          {sendError ? (
            <Text
              visual="smallRegular"
              color="text.error.dark"
              textAlign="right"
            >
              {sendError}
            </Text>
          ) : null}
        </Flex>
      )}

      {/* Change history */}
      {paymentRequest ? (
        <Flex flexDir="column" gap="150">
          <Flex
            justifyContent="space-between"
            alignItems="center"
            gap="150"
            flexWrap="wrap"
          >
            <Text visual="bodySemiBold">Change history</Text>
            {!isReadOnly ? (
              <Button
                size="sm"
                mode="light"
                onClick={() => setIsInternalNoteEditorOpen(true)}
                disabled={isInternalNoteEditorOpen}
              >
                {t("ADD_INTERNAL_NOTE")}
              </Button>
            ) : null}
          </Flex>
          {isInternalNoteEditorOpen ||
          changeHistoryNewestFirst.length > 0 ? (
            <Box
              borderWidth="1px"
              borderColor="border.light"
              borderRadius="md"
              p="200"
            >
              <Flex flexDir="column">
                {isInternalNoteEditorOpen ? (
                  <Flex
                    flexDir="column"
                    gap="150"
                    pb={changeHistoryNewestFirst.length ? "200" : "0"}
                    mb={changeHistoryNewestFirst.length ? "200" : "0"}
                    borderBottomWidth={
                      changeHistoryNewestFirst.length ? "1px" : undefined
                    }
                    borderColor="border.light"
                  >
                    <Textarea
                      value={internalNoteDraft}
                      onChange={e => setInternalNoteDraft(e.target.value)}
                      placeholder={t("INTERNAL_NOTE_PLACEHOLDER")}
                      rows={4}
                    />
                    <Flex justifyContent="flex-end" gap="100">
                      <Button
                        size="sm"
                        mode="light"
                        onClick={() => {
                          setIsInternalNoteEditorOpen(false);
                          setInternalNoteDraft("");
                        }}
                        disabled={isAddingInternalNote}
                      >
                        {t("INTERNAL_NOTE_CANCEL")}
                      </Button>
                      <Button
                        size="sm"
                        isLoading={isAddingInternalNote}
                        onClick={() => void handleSaveInternalNote()}
                      >
                        {t("INTERNAL_NOTE_SAVE")}
                      </Button>
                    </Flex>
                  </Flex>
                ) : null}
                {changeHistoryNewestFirst.map((entry: any, index: number) => {
                  const isInternalNote = entry.action === "internal_note";
                  const linkedPaymentEntry =
                    entry.paymentEntry ??
                    (entry.references?.paymentEntryId
                      ? paymentEntriesById.get(
                          String(entry.references.paymentEntryId),
                        )
                      : undefined);
                  const paymentDescription = linkedPaymentEntry
                    ? describeInvoiceJournalPayment(linkedPaymentEntry)
                    : undefined;
                  const actionLabel =
                    String(entry.action ?? "").toLowerCase() === "paid"
                      ? formatChangeHistoryAction(entry.action)
                      : (paymentDescription?.title ??
                        formatChangeHistoryAction(entry.action));
                  const showNote =
                    !!entry.note &&
                    !isRedundantPaymentRecordedNote(
                      entry.note,
                      paymentDescription?.detail,
                    );
                  return (
                    <Flex
                      key={`${entry.timestamp}-${entry.action}-${index}`}
                      flexDir="column"
                      gap="50"
                      py="150"
                      borderBottomWidth={
                        index < changeHistoryNewestFirst.length - 1
                          ? "1px"
                          : undefined
                      }
                      borderColor="border.light"
                      _first={{ pt: "0" }}
                      _last={{ pb: "0" }}
                    >
                      <Flex
                        justifyContent="space-between"
                        gap="150"
                        alignItems="flex-start"
                        flexWrap="wrap"
                      >
                        <Flex alignItems="center" gap="100" flexWrap="wrap">
                          <Text visual="smallSemiBold">{actionLabel}</Text>
                          {entry.performedByName ? (
                            <Text visual="smallRegular" color="text.light">
                              by {entry.performedByName}
                            </Text>
                          ) : null}
                        </Flex>
                        <Text visual="smallRegular" color="text.light">
                          {formatDate(entry.timestamp, "MMM dd yyyy")}
                        </Text>
                      </Flex>
                      {paymentDescription?.detail ? (
                        <Text visual="smallRegular" color="text.mid">
                          {paymentDescription.detail}
                        </Text>
                      ) : null}
                      {showNote ? (
                        <Text
                          visual="smallRegular"
                          color={isInternalNote ? "text.dark" : "text.mid"}
                          whiteSpace="pre-wrap"
                        >
                          {entry.note}
                        </Text>
                      ) : null}
                      {entry.activityLogId ? (
                        <Button
                          mode="light"
                          size="sm"
                          alignSelf="flex-start"
                          onClick={() =>
                            openEmailActivity(String(entry.activityLogId))
                          }
                        >
                          View email activity
                        </Button>
                      ) : null}
                      {legacyInvoicePdfUrl &&
                      String(entry.action).toLowerCase() === "created" ? (
                        <Button
                          mode="light"
                          size="sm"
                          alignSelf="flex-start"
                          onClick={() =>
                            window.open(
                              legacyInvoicePdfUrl,
                              "_blank",
                              "noopener,noreferrer",
                            )
                          }
                        >
                          View original invoice
                        </Button>
                      ) : null}
                    </Flex>
                  );
                })}
              </Flex>
            </Box>
          ) : (
            <Text color="text.mid">No history yet.</Text>
          )}
        </Flex>
      ) : null}

      {/* Revision modal */}
      <WhereaboutsModal
        isOpen={revisionModalOpen}
        onClose={() => setRevisionModalOpen(false)}
        isMaxZIndex
        title="Save invoice revision"
        description="Optional note for your team. Choose whether to email the payer with the updated total and pay link."
        cancelButtonProps={{ onClick: () => setRevisionModalOpen(false) }}
        saveButtonCopy="Save revision"
        saveButtonProps={{
          onClick: () => void confirmSaveRevision(),
          isLoading: isSaving,
        }}
      >
        <Flex flexDir="column" gap="250">
          <Input
            value={revisionNote}
            onChange={e => setRevisionNote(e.target.value)}
            label="Revision note (optional)"
            placeholder="e.g. Added discount, extended due date…"
          />
          <Flex alignItems="center" gap="200">
            <Switch
              checked={notifyRecipientOnRevision}
              onCheckedChange={e => setNotifyRecipientOnRevision(!!e?.checked)}
            />
            <Text visual="smallRegular">
              Email updated invoice to recipient
            </Text>
          </Flex>
        </Flex>
      </WhereaboutsModal>

      {/* Cancel confirmation modal */}
      <WhereaboutsModal
        isOpen={cancelModalOpen}
        onClose={() => setCancelModalOpen(false)}
        isMaxZIndex
        title="Cancel invoice"
        description="This cannot be undone. Please provide a reason for cancelling this invoice."
        cancelButtonProps={{ onClick: () => setCancelModalOpen(false) }}
        saveButtonCopy="Cancel invoice"
        saveButtonProps={{
          onClick: () => void handleCancel(),
          isLoading: isCancelling,
        }}
      >
        <Input
          value={cancellationNote}
          onChange={e => setCancellationNote(e.target.value)}
          label="Cancellation note"
          placeholder="Why was this invoice cancelled?"
        />
      </WhereaboutsModal>

      {/* Undo pending confirmation modal */}
      <WhereaboutsModal
        isOpen={undoPendingModalOpen}
        onClose={() => setUndoPendingModalOpen(false)}
        isMaxZIndex
        title="Undo 'pending' status?"
        cancelButtonProps={{ onClick: () => setUndoPendingModalOpen(false) }}
        saveButtonCopy="Undo pending status"
        saveButtonProps={{
          onClick: () => void handleUndoPending(),
          isLoading: isUndoingPending,
        }}
      >
        <Text color="text.mid">
          If you continue, this invoice will be returned to 'sent' status. This
          means that recipients will be able to pay by credit card.
        </Text>
      </WhereaboutsModal>

      <WhereaboutsModal
        isOpen={legacyMarkPaidModalOpen}
        onClose={() => setLegacyMarkPaidModalOpen(false)}
        isMaxZIndex
        title="Mark invoice as paid"
        description="This will create the order from this invoice and mark it as paid."
        cancelButtonProps={{
          onClick: () => setLegacyMarkPaidModalOpen(false),
        }}
        saveButtonCopy="Mark as paid"
        saveButtonProps={{
          onClick: () => void handleLegacyMarkPaid(),
          isLoading: isMarkingPaid,
        }}
      >
        <Input
          label="Note (optional)"
          value={legacyMarkPaidNote}
          onChange={event => setLegacyMarkPaidNote(event.target.value)}
        />
      </WhereaboutsModal>
      <WhereaboutsModal
        isOpen={resendModalOpen}
        onClose={() => setResendModalOpen(false)}
        isMaxZIndex
        title="Resend invoice"
        description="Send the current invoice again without creating a revision."
        cancelButtonProps={{ onClick: () => setResendModalOpen(false) }}
        saveButtonCopy="Resend invoice"
        saveButtonProps={{
          onClick: () => void handleResend(),
          isLoading: isResending,
        }}
      >
        <Input
          label="Note (optional)"
          value={resendNote}
          onChange={event => setResendNote(event.target.value)}
        />
      </WhereaboutsModal>
      <InvoiceRecordPaymentModal
        isOpen={recordPaymentModalOpen}
        onClose={() => setRecordPaymentModalOpen(false)}
        paymentRequestId={activePaymentRequestId}
        onCompleted={async () => {
          await refetch();
        }}
      />
      <InvoiceRecordPaymentModal
        isOpen={settleModalOpen}
        onClose={() => setSettleModalOpen(false)}
        paymentRequestId={activePaymentRequestId}
        settleWithoutSending
        onCompleted={async () => {
          await refetch();
        }}
      />
      <InvoiceJournalModal
        isOpen={journalModalOpen}
        onClose={() => setJournalModalOpen(false)}
        paymentRequestId={activePaymentRequestId}
        onChanged={async () => {
          await refetch();
        }}
      />

      {/* Edit invoice number modal — draft/for-review only */}
      <WhereaboutsModal
        isOpen={editInvoiceNumberModalOpen}
        onClose={() => setEditInvoiceNumberModalOpen(false)}
        isMaxZIndex
        title={t("EDIT_INVOICE_NUMBER")}
        description={t("EDIT_INVOICE_NUMBER_HINT")}
        cancelButtonProps={{
          onClick: () => setEditInvoiceNumberModalOpen(false),
        }}
        saveButtonProps={{
          onClick: () => void handleSaveInvoiceNumber(),
          isLoading: isSavingInvoiceNumber,
        }}
      >
        <Flex flexDir="column" gap="150">
          <Input
            value={invoiceNumberInput}
            onChange={e => handleInvoiceNumberInputChange(e.target.value)}
            maxLength={30}
            placeholder="e.g. AB12CD34"
            fontFamily="mono"
          />
          {invoiceNumberError ? (
            <Text visual="smallRegular" color="text.error.dark">
              {invoiceNumberError}
            </Text>
          ) : null}
        </Flex>
      </WhereaboutsModal>

      {/* Invoice number locked modal — shown for sent/paid/cancelled invoices */}
      <WhereaboutsModal
        isOpen={invoiceNumberLockedModalOpen}
        onClose={() => setInvoiceNumberLockedModalOpen(false)}
        isMaxZIndex
        title={t("INVOICE_NUMBER_LOCKED_TITLE")}
        customFooter={
          <Flex
            justifyContent="flex-end"
            gap="150"
            px={{ base: "300", md: "500" }}
            py={{ base: "300", md: "400" }}
          >
            <Button
              size="lg"
              onClick={() => setInvoiceNumberLockedModalOpen(false)}
            >
              {t("CLOSE")}
            </Button>
          </Flex>
        }
      >
        <Text color="text.mid">{t("INVOICE_NUMBER_LOCKED_BODY")}</Text>
      </WhereaboutsModal>

      <EmailActivitySlideout
        isOpen={emailSlideoutOpen}
        onClose={() => {
          setEmailSlideoutOpen(false);
          setEmailActivityLog(null);
        }}
        activityLog={emailActivityLog}
        zIndex="max"
      />
    </Flex>
  );
};
